/**
 * @title The Ciao Prolog playground
 * 
 * This file implements several components for editing Prolog files
 * and executing them interactively using Ciao toplevel processes.
 * The editor component is based on the Monaco editor. The Ciao
 * toplevel makes use of CiaoWasm to run locally in the browser.
 *
 * @author The Ciao Development Team
 */

/* (must be loaded from lpdoc.js) */

/* --------------------------------------------------------------------------- */
// * Playground defaults

const splash_code_pl = `\
% Write your Prolog code here, e.g.:

app([],X,X).
app([X|L1],L2,[X|L3]) :-
     app(L1,L2,L3).

% Some things you can do: 
% - Press 'Load' and you can then type your 
%   queries in the other pane, e.g.:
%
% ?- app([1,2],[3,4],L).
% ?- app(A,B,[1,2,3,4]).
%
% - Click on the magnifier button to run the debugger.
% - Load some examples.
% - Click on the book icon to generate documentation.

% - Choose 'New document' to start a new
%   notebook (an 'active logic document')!
`;
const splash_code_md = `\
\\title A simple Active Logic Document

This is a sample **active logic document**: a **notebook** containing
_embedded runnable Prolog code examples_, that can be edited,
queried, shared etc. and _runs locally in the browser, with no need for
a server_.

You can use:

- **Markdown** (LPdoc flavor)
- or @bf{LPdoc commands}. 

Most importantly, you can include **runnable examples**:

\`\`\`ciao_runnable
:- module(_,_).

app([],X,X).
app([X|L1],L2,[X|L3]) :-
     app(L1,L2,L3).
\`\`\`

You can edit the example code and click on the \`?\` sign to load it.
Then you can then **query** the examples:

\`\`\`ciao_runnable
  ?- app([1,2],[3,4],L).
\`\`\`

\`\`\`ciao_runnable
  ?- app(A,B,[1,2,3,4]).
\`\`\`

Some things to try **in the document**:

  - Edit the example above.
  - (Re)load it by clicking on the \`?\`.
  - Run queries in the query box. 
  - Open the example in the playground with the arrow.
  - If in presentation (fullscreen) mode, go/return to the editor by 
    clicking on the pencil icon in the top right.

Some things to try in the **editor/playground**:

  - Edit the markdown source and hit the \`Load\` button. 
  - Click on the arrows button to toggle presentation mode and see just the document. 
    You can go back to the editor clicking on the pencil in the top right. 
  - Use the \`Share!\` button to generate a URL for the document that you can send, publish, etc.
  - \`Save\` the document source to your computer and \`Open\` it at any other time.
  - Click on the \`Toggle on-the-fly\` button to see the document change as the markdown is changed.

You can save the document source with the 'Save'
button and upload it at any other time.

You can also click on the 'Share!' button to obtain a URL
that contains the document and which you can paste in other
documents, send by email, etc. (see 
[**this chapter**](/ciao/build/doc/ciao_playground.html/ciao_playground_embedding.html))
of the manual).

Check out the [**markdown
syntax**](/ciao/build/doc/lpdoc.html/Markdown.html) and the
[**commands for including editable and runnable
examples**](/ciao/build/doc/lpdoc.html/Runnables.html)!
`;

// Helper for menus
function menu_h(str) { // menu header
  return { k:'', n:`<div style="font-weight: bold">${str}</div>` };
}
function menu_hlink(str, href) { // menu header with link
  return { k:'', n:`<a style="padding-left:0px;" href="${href}"><div style="font-weight: bold">${str} &#8599;</div></a>` };
}
function menu_i(str, file) { // menu indented item
  return { k:file, n:'&#x1F4C4; '+str };
}

const playgroundCfg_defaults = {
  title: "Prolog playground",
  with_header: true,
  with_github_stars: true,
  has_new_button: true,
  has_ext_mode_button: true,
  has_file_button: true,
  //
  has_load_button: true,
  has_doc_button: true,
  has_debug_button: true,
  has_toggle_presentation_button: true,
  has_toggle_on_the_fly_button: true,
  has_run_tests_button: true,
  has_acheck_button: true,
  has_spec_button: true,
  has_browse_analysis_opts_button: true,
  has_lptp_button: false,
  //
  has_share_button: true,
  //
  has_layout_button: true,
  has_examples_button: true,
  window_layout: ['E','T'],
  //
  storage_key: 'code', // key for local storage of draft program
  //
  splash_dialog: true,
  // a dictionary for each accepted file ext, or just a string (legacy)
  // Note: we use those keys to enable/disable each particular new button
  splash_code: {
    '.pl': splash_code_pl, 
    '.md': splash_code_md,
    '.lpdoc': splash_code_md // TODO: just testing so that .lpdoc files can also be opened
  }, 
  example_list: [
    // TODO: move this collection of links outside the code?
    //       also, point to a tutorial (an ALD)

    // ---------------------------------------------------------------------------
    // TRADITIONAL PROLOG: 
    menu_h("Traditional Prolog"),
    menu_i('Population query', 'core/examples/general/population_query.pl'),
    menu_i('Simple puzzle', 'lpdoc/examples/puzzle.pl'),
    menu_i('Bignums', 'core/examples/general/bignums.pl'),
    // menu_i('Boyer Prover', 'core/examples/general/boyer.pl'),
    menu_i('Crypto-multiplication', 'core/examples/general/crypt.pl'),
    // menu_i('Fibonacci', 'core/examples/general/fib.pl'),
    // menu_i('Fast Fourier Transform', 'core/examples/general/fft.pl'),
    // menu_i('Guradians puzzle', 'core/examples/general/guardians.pl'),
    // menu_i('Jugs puzzle', 'core/examples/general/jugs.pl'),
    menu_i('Knights puzzle', 'core/examples/general/knights.pl'),
    menu_i('N Queens', 'core/examples/general/nqueens.pl'),
    // menu_i('Poly', 'core/examples/general/poly.pl'),
    menu_i('Primes', 'core/examples/general/primes.pl'),
    menu_i('Quick-sort', 'core/examples/general/qsort.pl'),
    // menu_i('Takeuchi', 'core/examples/general/tak.pl')
    // ---------------------------------------------------------------------------
    // 99 problems
    menu_hlink("99 Prolog problems", "https://cliplab.org/logalg/doc/99problemsALD.html/"),
    // ---------------------------------------------------------------------------
    // ALDs
    menu_hlink("Documents", "/ciao/build/doc/ciao_playground.html/ciao_playground_embedding.html"),
    menu_i('Minimal notebook', 'lpdoc/examples/min_ald.md'), 
    // menu_i('min_ald.lpdoc', 'lpdoc/examples/min_ald.lpdoc'),  // For testing with .lpdoc suffix
    menu_i('Simple puzzle', 'lpdoc/examples/puzzle_ald.md'),
    menu_i('Factorial exercise', 'lpdoc/examples/factorial_peano_iso.md'),
    menu_i('Slides example', 'lpdoc/examples/slides_fairsr.md'),
    // ---------------------------------------------------------------------------
    // Functional: 
    menu_hlink("Functional notation", "/ciao/build/doc/ciao.html/fsyntax_doc.html"),
    menu_i('Population query', 'core/examples/general/population_query_f.pl'),
    menu_i('Simbolic derivation', 'core/examples/general/derivf.pl'),
    menu_i('Naive reverse', 'core/library/fsyntax/examples/revf.pl'),
    menu_i('Factorial', 'core/library/fsyntax/examples/factf.pl'),
    menu_i('Lazy Fibonacci', 'core/library/fsyntax/examples/lazyex.pl'),
    // ---------------------------------------------------------------------------
    // SR: 
    menu_hlink("Search rules", "/ciao/build/doc/ciao.html/bf_doc.html"),
    menu_i('Peano arithmetic', 'core/library/sr/examples/peano.pl'),
    // ---------------------------------------------------------------------------
    // Documentation: /ciao/build/doc/lpdoc.html/
    // menu_i('<div style="color:var(--header); background-color:var(--bg)"> Documentation</div>', ''),
    // ---------------------------------------------------------------------------
    // menu_i('ctest_math.pl', 'lpdoc/examples/test_math.pl'),
    // ---------------------------------------------------------------------------
    // Assertions: (Just types, modes, etc. for now)
    menu_hlink("Modes, types, etc.", "/ciao/build/doc/ciaopp_tutorials.html/tut_assrts.html"),
    // menu_i('incompatible_type_base.pl', 'ciaopp/examples/assrt-tutorial/incompatible_type_base.pl'),
    // menu_i('incompatible_type_base_f.pl', 'ciaopp/examples/assrt-tutorial/incompatible_type_base_f.pl'),
    // menu_i('incompatible_type_f_error.pl', 'ciaopp/examples/assrt-tutorial/incompatible_type_f_error.pl'),
    // menu_i('incompatible_type_f_fixed.pl', 'ciaopp/examples/assrt-tutorial/incompatible_type_f_fixed.pl'),
    menu_i('QSort - some warnings', 'ciaopp/examples/assrt-tutorial/qsort_noassrt_warnings.pl'),
    menu_i('QSort - nowarnings', 'ciaopp/examples/assrt-tutorial/qsort_noassrt_nowarnings.pl'),
    menu_i('QSort - assertions verified', 'ciaopp/examples/assrt-tutorial/qsort_assrt_det.pl'),
    menu_i('QSort - may fail detected', 'ciaopp/examples/assrt-tutorial/qsort_det_may_fail.pl'),
    menu_i('QSort - nondet detected', 'ciaopp/examples/assrt-tutorial/qsort_det_nondet.pl'),
    menu_i('Qsort - doccomments', 'ciaopp/examples/assrt-tutorial/qsort_modes_doccomments_det.pl'),
    // menu_i('revf_n_o_det_error.pl', 'ciaopp/examples/assrt-tutorial/revf_n_o_det_error.pl'),
    // menu_i('revf_n_o_det_error.pl', 'ciaopp/examples/assrt-tutorial/revf_n_o_det_error.pl'),
    // menu_i('revf_n_o_det_verified.pl', 'ciaopp/examples/assrt-tutorial/revf_n_o_det_verified.pl'),
    // menu_i('revf_n_ub_det_error.pl', 'ciaopp/examples/assrt-tutorial/revf_n_ub_det_error.pl'),
    // menu_i('revf_n_ub_det_verified.pl', 'ciaopp/examples/assrt-tutorial/revf_n_ub_det_verified.pl'),
    // ---------------------------------------------------------------------------
    // Constraints:
    menu_hlink("Constraints (clpfd)", "/ciao/build/doc/ciao.html/clpfd_doc.html"),
    menu_i('Send more money', 'core/examples/general/money_clpfd.pl'),
    menu_i('Sudoku', 'core/examples/general/sudoku_clpfd.pl'),
    menu_i('N Queens', 'core/examples/general/nqueens_clpfd.pl'),
    // ---------------------------------------------------------------------------
    // Etc.:
    menu_hlink("More...", "/documentation.html")
  ],
  // Delay for saving when content changes (milliseconds)
  // (do not set too low if files are large)
  autosave_delay: 200,
  // Amend code (add module, etc.)
  amend_on_save: true,
  // Auto-* actions (on start and restart)
  auto_action: null, // This set later depending on whether .md, .pl, etc.
  // Do auto-* actions on the fly (as document changes)
  on_the_fly: false,
  // Keep worker alive (only when window.lpdocPG=='runnable' at this moment)
  runnable_keep_alive: true
};

// TODO: make it nicer
// (Config for miniplayground)
var miniPlaygroundCfg = {
  with_header: false,
  has_new_button: false,
  has_ext_mode_button: false,
  has_file_button: false,
  //
  has_load_button: false,
  has_doc_button: false,
  has_debug_button: false,
  has_toggle_presentation_button: false,
  has_toggle_on_the_fly_button: false,
  has_run_tests_button: false,
  has_acheck_button: false,
  has_spec_button: false,
  has_browse_analysis_opts_button: false,
  //
  has_share_button: false,
  //
  has_layout_button: false,
  on_the_fly: true,
  storage_key: null // (program is never stored)
};

/* --------------------------------------------------------------------------- */
// * File extensions 

import { file_ext_def, file_ext_is_doc } from './file_exts.js';

/* Get an array of active file extensions in this playground */
function get_allowed_file_exts(pgCfg) {
  if (typeof pgCfg.splash_code === 'string') { // legacy
    return ['.pl'];
  } else { // get extensions from splash code
    return Object.keys(pgCfg.splash_code);
  }
}

/* Get splash code for the given extension */
function get_splash_code_for_ext(pgCfg, ext) {
  let str;
  if (typeof pgCfg.splash_code === 'string') { // legacy
    str = pgCfg.splash_code;
  } else {
    str = pgCfg.splash_code[ext];
  }
  return {str:str, ext:ext, origin:'splash'};
}

/* Default file extension for splash code */
function splash_default_ext(pgCfg) {
  return get_allowed_file_exts(pgCfg)[0];
}

// ---------------------------------------------------------------------------
// * LPTP extension

import {
  send_lptp_buffer,
  get_lptp_buffer,
  lptp_tactic,
  print_lptp_definition,
  list_lptp_facts,
  lptp_mark
} from './pg_lptp.js';

/* --------------------------------------------------------------------------- */

// lpdocPG:
//   'raw': do not setup pgset (custom setups)
//   'runnable': setup runnable code blocks in a LPdoc document
//   'playground': setup a playground
if (window.lpdocPG === 'undefined') {
  console.error('loading ciao_playground.js without lpdocPG');
  window.lpdocPG = 'raw';
}

// ===========================================================================
// * Dynamically load dependencies

// TODO: not very fast but robust enough; better ways?
(function() {
  //importCSS('/playground/css/ciao_playground.css'); // (better from main html)
  // Ciao menu html library // TODO: modularize (this requires changes in ciao_vsc)
  importScript(res_URL('/playground/js/ciao_menu.js'));
  // Ciao engine and interface (ciaowasm)
  importScript(res_URL('/js/ciao-prolog.js'));
})();

// ---------------------------------------------------------------------------
// * Editor component

// // CodeMirror-based editor
// import { create_pg_editor, editor_update_theme } from './cm/editor_cm.js';

// Monaco-based editor
import { create_pg_editor, editor_update_theme } from './editor.js';

// ---------------------------------------------------------------------------
// * UI - Main playground cell class

import Split from 'split.js'; // (external)
import { Modal, btn_dropdown_act, Layout, layout_modif_list, Vis } from './ui_aux.js';
import { get_icon } from './svg_icons.js';

/** A playground cell with an IDE layout */
class PGCell {
  constructor(cproc) {
    this.pgCfg = Object.assign({...playgroundCfg_defaults}, globalPlaygroundCfg); // TODO: allow as param?
    this.code_ext = null;
    this.editor = null;
    this.toplevel = null;
    this.previewEd = null; // TODO: extend to multiple editor buffers
    // Autosave
    this.autosave_timer = null; // timer
    this.autosave_redo = false; // needs redo
    //
    this.debugging = false; // TODO: fixme
    this.cproc = cproc; // a ToplevelProc
    //
    this.vis = new Vis(); // visibility state
  }

  /** Executed by ToplevelProc on (re)start */
  // TODO: one cproc may have several pg attached! (just read/write to the top one)
  async on_cproc_start() {
    this.#cancel_autosave();
    if (this.is_R) {
      if (this.get_auto_action() === 'load') {
        if (this.pgset !== null) await this.pgset.reset_all_code();
        //this.cproc.comint = this.toplevel; // (re)attach to this pg comint // TODO: remove this line if everything is OK
      }
    } else {
      await process_code(this); 
    }
  }

  /** Set code and process */
  async set_code_and_process(code) {
    if (!this.is_R) { // Save if we are not in a runnable
      pers_set_code(this.pgCfg, code);
    }
    this.set_auto_action(file_ext_def[code.ext].action);
    this.set_editor_value(code);
    this.update_menu_buttons(); // TODO: needed? menu buttons do not change here (JF)
    this.#cancel_autosave();
    if (!this.is_R) { // TODO: treat is_R == true case
      await process_code(this);
    }
  }

  /** Set/get auto action */
  set_auto_action(action) {
    // TODO: set a field in the PGCell instead
    this.pgCfg.auto_action = action;
  }
  get_auto_action() {
    // TODO: set a field in the PGCell instead
    return this.pgCfg.auto_action;
  }

  /* ---------------------------------------------------------------------- */
  /** Set up layout */
  async setup(base_el, cell_data, pgset) {
    this.pgset = pgset;
    this.cell_data = cell_data;
    //
    if (this.cell_data.kind === 'full' || this.cell_data.kind === 'miniplayground') {
    } else {
      this.is_R = true;
    }
    if (this.is_R) {
      base_el.classList.add("lpdoc-runnable-container");
      base_el.replaceChildren();
    }
    //
    if (!this.is_R) {
      if (this.cell_data.kind === 'full') {
      } else if (this.cell_data.kind === 'miniplayground') {
        setup_mini_pg(this, base_el);
      } else {
        return; // TODO: what is this case?
      }
    }
    // Configurable layout
    if (!this.is_R) {
      this.splits = [];
      this.vis.set('layout', Layout.create(this.pgCfg.window_layout));
    }
    // Header
    this.header_el = null;
    if (!this.is_R && this.pgCfg.with_header) {
      this.header_el = create_header(this.pgCfg);
      base_el.appendChild(this.header_el);
    }
    // Menu
    this.menu_el = null;
    this.ext_mode_button_el = undefined; // nothing
    this.load_button_el = undefined; // nothing
    this.debug_button_el = undefined; // nothing
    this.more_button_el = undefined; // nothing
    this.on_the_fly_button_el = undefined; // nothing
    if (this.is_R) {
      this.#setup_menu_R(base_el);
    } else {
      this.#setup_menu(base_el);
    }
    // Editor
    let initial_code = initial_code_from_cell_data(this.cell_data);
    if (initial_code !== null) {
      this.#setup_editor(initial_code);
      this.set_auto_action(file_ext_def[this.code_ext].action);
      this.update_menu_buttons(); // TODO: why only here? (JF)
    } else {
      this.editor_el = null;
    }
    if (!this.is_R) {
      // Turn presentation mode on for docs fetched from URL
      if (this.pgCfg.has_toggle_presentation_button &&
          file_ext_is_doc(this.code_ext) &&
          (this.cell_data.origin === 'URL' || this.cell_data.origin === 'URL_link')) {
        this.vis.set('presentation', true); // TODO: only when loading from URL?
      } else {
        this.vis.set('presentation', false);
      }
    }
    // Toplevel
    this.#setup_toplevel();
    // Preview
    this.#setup_preview();
    // Setup window layout
    this.#setup_layout(base_el);
    // Start cell (if needed)
    if (this.is_R) {
      if (this.cell_data.kind == 'query') {
        await this.enable_response({onequery: true, autoresize:true});
        await this.cproc.ensure_started(this.toplevel);
      } else if (this.cell_data.kind == 'code') {
        await this.enable_response({noprompt: true, autoresize:true, noshrink: true});
        this.toplevel_el.className = 'comint-echo-hidden'; // TODO: override (hide until by default)
        await this.cproc.ensure_started(this.toplevel);
      } else if (this.cell_data.kind == 'jseval') { // (expects an async function in 'jscode')
        let f = Function('"use strict";return (' +
                         this.cell_data['jscode'] +
                         ')')();
        await f(this);
      }
    } else {
      await this.cproc.ensure_started(this.toplevel);
    }
    // Write (without evaluating) default query (once started)
    if (this.is_R) {
      if (this.cell_data.kind == 'query') {
        this.toplevel.clear_output();
        this.toplevel.print_prompt();
        this.toplevel.display(this.cell_data['query']);
      }
    }
  }

  /** Editor menu (menubar) */
  #setup_menu(base_el) {
    let menu_el = elem_cn('div', 'main-menu');
    // (layout)
    if (this.pgCfg.has_layout_button) this.#setup_layout_button(menu_el);
    // (create/file options)
    if (this.pgCfg.has_new_button) this.#setup_new_button(menu_el);
    if (this.pgCfg.has_file_button) this.#setup_file_button(menu_el);
    if (this.pgCfg.has_examples_button) this.#setup_examples_button(menu_el);
    if (this.pgCfg.has_ext_mode_button) this.#setup_ext_mode_button(menu_el);
    // (actions)
    if (this.pgCfg.has_load_button) this.#setup_load_button(menu_el);
    if (this.pgCfg.has_debug_button) this.#setup_debug_button(menu_el);
    if (this.pgCfg.has_doc_button) this.#setup_doc_button(menu_el);
    if (this.pgCfg.has_toggle_presentation_button) this.#setup_toggle_presentation_button(menu_el);
    if (this.pgCfg.has_toggle_on_the_fly_button) this.#setup_toggle_on_the_fly_button(menu_el);
    // (advanced actions)
    if (this.pgCfg.has_lptp_button) this.#setup_lptp_buttons(menu_el);
    this.#setup_advanced_buttons(menu_el);

    // (right menu part)
    const right_menu_el = elem_cn('div', 'right-menu');
    menu_el.appendChild(right_menu_el);

    if (this.pgCfg.has_share_button) this.#setup_share_button(right_menu_el);

    //
    this.menu_el = menu_el;
    base_el.appendChild(menu_el);
  }

  /** Editor menu (lpdocPG === 'runnable') */
  #setup_menu_R(base_el) {
    const menu_el = elem_cn('div', 'lpdoc-runnable-buttons');
    this.status_el = null; // TODO: set status_el to null before
    if (this.cell_data.kind == 'query') { // menu for running queries
      menu_el.appendChild(btn('lpdoc-runnable-button', "Run query", "<span>&#9654;</span>", (() => {
        this.toplevel.treat_enter().then(() => {});
      })));
    } else if (this.cell_data.kind == 'code' || this.cell_data.kind == 'exercise' || this.cell_data.kind == 'exfilter' || this.cell_data.kind == 'exfilterex') {
      this.status_el = elem_cn('span', 'lpdoc-runnable-statusmark');
      let btn_l;
      let btn_c;
      if (this.cell_data.kind == 'code') { // menu for loading (focused) code
        btn_l = "Load";
        btn_c = (() => {
          this.with_response(load_code).then(() => {});
        });
      } else if (this.cell_data.kind == 'exfilter') { // menu for exfilter
        btn_l = "Show analysis";
        btn_c = (() => {
          this.with_response(run_exfilter).then(() => {});
        });
      } else if (this.cell_data.kind == 'exfilterex') { // menu for exfilter
        btn_l = "Submit answer";
        btn_c = (() => {
          this.with_response(run_exfilter_exercise).then(() => {});
        });
      } else if (this.cell_data.kind == 'exercise') { // menu for running tests
        btn_l = "Run tests";
        btn_c = (() => {
          this.with_response(run_tests).then(() => {});
        });
      }
      let btn_el = btn('lpdoc-runnable-button', btn_l, '', btn_c);
      btn_el.appendChild(this.status_el);
      menu_el.appendChild(btn_el);
      //
      menu_el.appendChild(btn('lpdoc-runnable-button', "Load in playground", "<span>&#8599;</span>", (() => {
        this.load_in_playground();
      })));
    }
    //
    // this.menu_el = menu_el; // TODO: not needed
    base_el.appendChild(menu_el);
  }

  /** The code editor (creates editor_el and editor) */
  #setup_editor(code) {
    this.code_ext = code.ext;
    let kind = file_ext_def[code.ext].kind;
    if (this.is_R) {
      this.editor_el = elem_cn('div', 'lpdoc-runnable-editor-container');
      this.editor = create_pg_editor(this.editor_el, code.str, kind, {autoresize: true});
      this.editor.on_change_content(e => {
        this.set_code_status('unknown');
      });
      this.set_code_status('unknown'); // set code status (if applicable)
    } else {
      this.editor_el = elem_cn('div', 'editor-container');
      // TODO: make 'collab' optional
      this.editor = create_pg_editor(this.editor_el, code.str, kind, {collab: false/*true*/});
      add_playground_bindings(this.editor, this);
      // Save to local storage when content changes (with a delay)
      this.editor.on_change_content(e => {
        this.autosave_redo = true;
        this.#sched_autosave();
      });
    }
  }

  /** Comint (toplevel) */
  #setup_toplevel() {
    if (this.is_R) {
      this.toplevel_el = elem_cn('div', 'lpdoc-runnable-comint');
    } else {
      this.toplevel_el = elem_cn('div', 'comint-container');
      this.toplevel = new Comint(this.toplevel_el, this, {}); // commit for toplevel
    }
  }

  /** Preview */
  #setup_preview() {
    if (this.is_R) {
      this.preview_el = elem_cn('div', 'lpdoc-runnable-preview');
    } else {
      this.preview_el = elem_cn('div', 'preview-container');
    }
    this.preview_doc = null; // nested doc/view (if needed)
  }

  /** Help footer */
  #setup_help_footer(base_el) {
    const el = btn('lpdoc-runnable-help', "Copy the solution", "&#9733; Show solution", () => {
      let sol = this.cell_data['solution'];
      (async() => {
        await this.set_code_and_process({str:sol,ext:'.pl'});
      })();
    });
    this.help_el = el;
    base_el.appendChild(this.help_el);
  }

  /** Setup internal layout */
  #setup_layout(base_el) {
    if (this.is_R) {
      if (this.editor_el !== null) base_el.appendChild(this.editor_el);
      base_el.appendChild(this.toplevel_el);
      base_el.appendChild(this.preview_el);
      // Help footer
      if (this.cell_data.kind == 'exercise' || this.cell_data.kind == 'exfilterex') {
        this.#setup_help_footer(base_el);
      }
    } else {
      this.layout_el = elem_cn('div', 'playground');
      base_el.appendChild(this.layout_el);
      //
      this.vis.set('narrow', (window.matchMedia('(max-width: 750px)').matches ? true : false));
      this.update_inner_layout(); // (initial setup)
      window.matchMedia('(max-width: 750px)').addEventListener('change', () => {
        this.vis.set('narrow', (window.matchMedia('(max-width: 750px)').matches ? true : false));
        this.update_inner_layout();
      });
    }
  }

  /** Update (internal) layout (incrementally) */
  update_inner_layout() {
    if (this.is_R) return;
    let update_dim = false;
    // Handle 'narrow' updates
    this.vis.inc_update('narrow', (st) => {
      this.vis.force_update('layout'); // Force layout update
    });
    // Handle 'presentation' updates
    this.vis.inc_update('presentation', (st) => {
      // TODO: less ad-hoc way?
      if (st) {
        document.body.style.padding = '0px';
        this.preview_el.style.border = '0px';
        if (this.header_el != null) this.header_el.style.display = "none";
        if (this.menu_el != null) this.menu_el.style.display = "none";
      } else {
        document.body.style.padding = '8px';
        this.preview_el.style.border = '1px solid var(--border)';
        if (this.header_el != null) this.header_el.style.display = "block";
        if (this.menu_el != null) this.menu_el.style.display = "flex";
      }
      this.vis.force_update('layout'); // Force layout update
    });
    // (Re)create window layout if needed
    this.vis.inc_update('layout', (layout) => {
      this.#destroy_splits(); // remove split views before wiping out layout_el
      //
      let ls;
      let presentation_mode = this.vis.get('presentation');
      if (presentation_mode) {
        ls = 'P'; // only preview
      } else {
        ls = layout.get_ls(this.vis.get('narrow'));
      }
      //
      this.layout_el.replaceChildren();
      if (presentation_mode) {
        // (button to go back from presentation mode)
        let el = elem_cn('div', 'lpdoc-runnable-buttons');
        el.style.marginTop = '25px'; // TODO: use other class?
        el.style.marginRight = '10px'; // TODO: use other class?
        let btn_el = btn('lpdoc-runnable-button', "Edit mode", '', (() => {
          toggle_presentation(this).then(() => {}); // TODO: use "async () => { ... }" instead?
        }));
        btn_el.appendChild(get_icon('edit'));
        btn_el.classList.add("opacity-transition");
        el.appendChild(btn_el);
        this.layout_el.appendChild(el);
      }
      let el = this.#gen_layout(ls);
//      if (!el.classList.contains('preview-container')) {
        el.style.height = '100%'; // TODO: why? better way?
//      }
      this.layout_el.appendChild(el);
      //
      update_dim = true;
    });
    this.toplevel.update_inner_layout();
    if (update_dim) {
      this.update_dimensions();
      if (this.vis.get('presentation')) this.preview_el.focus(); // set focus
    }
  }

  #gen_layout(s) {
    let e;
    if (Array.isArray(s)) {
      let el1 = this.#gen_layout(s[1]);
      let el2 = this.#gen_layout(s[2]);
      e = this.#setup_split(s[0], el1, el2);
    } else {
      switch(s) {
      case 'E': e = this.editor_el; break;
      case 'P': e = this.preview_el; break;
      case 'T': e = this.toplevel_el; break;
      }
    }
    return e;
  }

  /** Destroy splits */
  #destroy_splits() {
    for (const s of this.splits) { s.destroy(); }
    this.splits = [];
  }

  /** Create a split resizable div (s directon, el1, el2 elems) */
  #setup_split(s, el1, el2) {
    let opts = {};
    let el = document.createElement("div");
    if (s === 'h') {
      opts.direction = 'horizontal';
      opts.cursor = 'col-resize';
      el.classList.add('split-horiz');
    } else if (s === 'v') {
      opts.direction = 'vertical';
      opts.cursor = 'row-resize';
      el.classList.add('split-vert');
    }
    el.appendChild(el1);
    el.appendChild(el2);
    opts.gutterSize = 8;
    opts.onDrag = (sizes) => {
      this.update_dimensions();
    };
    this.splits.push(Split([el1, el2], opts));
    return el;
  }

  /* ---------------------------------------------------------------------- */

  /** Make version visible */
  show_version(str) {
    let info_match = str.match(/.*^(Ciao.*$).*/m);
    if (info_match != null && info_match.length == 2) {
      if (this.toplevel_el !== null && this.toplevel_el !== undefined) {
        let base;
        let footerElems = null;
        /* Try to find a footer elem under the closest lpdoc-main ancestor */
        base = this.toplevel_el.closest(".lpdoc-main"); // preview
        if (base !== null) {
          footerElems = base.getElementsByClassName("lpdoc-footer");
          if (footerElems.length == 0) footerElems = null;
        }
        /* Otherwise, look under the whole document */ // TODO: needed for website, better way?
        if (footerElems === null) {
          footerElems = document.getElementsByClassName("lpdoc-footer");
          if (footerElems.length == 0) footerElems = null;
        }
        /* Patch footer with version */
        if (footerElems !== null) {
          footerElems[0].innerHTML = "Generated with LPdoc | <span style='color:var(--face-checked-assrt)'>RUNNING</span> " + info_match[1];
        }
      }
    }
    if (toplevelCfg.statistics) console.log(str);
  }

  /* ---------------------------------------------------------------------- */

  /** Set window layout (not visible until update_inner_layout() is called) */
  set_window_layout(modifs) {
    this.vis.set('layout', Layout.create(modifs));
  }

  show_toplevel(m) { 
    if (this.is_R) return; /* (do nothing) */
    this.vis.get('layout').t = m;
    this.update_layout_sel_button_marks();
  }

  show_preview(m) {
    if (this.is_R) {
      if (m === false) {
        this.preview_el.style.display = "none";
      } else {
        this.preview_el.style.display = "block";
      }
    } else {
      // TODO: mode is not preserved when going from false to true
      if (m !== false && this.vis.get('layout').p !== false) return; // keep previous mode
      this.vis.get('layout').p = m;
      this.update_layout_sel_button_marks();
    }
  }

  async enable_response(opts) {
    if (this.toplevel === null) {
      const e = this.toplevel_el;
      e.className = '';
      e.style.marginTop = '5px';
      this.toplevel = new Comint(e, this, opts);
    }
  }

  /* ---------------------------------------------------------------------- */

  /** Any of the editors have focus */
  has_focus() {
    return ((this.editor !== null && this.editor.has_focus()) ||
            (this.toplevel !== null && this.toplevel.has_focus()) ||
            (this.previewEd !== null && this.previewEd.has_focus()) ||
            (this.preview_doc !== null && this.preview_doc.has_focus()));
  }

  /* ---------------------------------------------------------------------- */

  /** Update layout of editors (after redimensioning) */
  update_dimensions() {
    if (this.editor !== null) this.editor.update_dimensions();
    if (this.toplevel !== null) this.toplevel.update_dimensions();
    if (this.previewEd !== null) this.previewEd.update_dimensions();
    if (this.preview_doc !== null) this.preview_doc.update_dimensions();
  }

  /* ---------------------------------------------------------------------- */

  /** Change focus (editor <-> toplevel). */
  change_focus() {
    if (this.editor.has_focus()) {
      this.toplevel.editor.set_focus();
    } else {
      this.editor.set_focus();
    }
  }

  /* ---------------------------------------------------------------------- */

  /** Schedule autosave */
  #sched_autosave() {
    if (this.autosave_timer !== null) return; // Already set, do nothing
    this.autosave_timer = setTimeout(() => {
      this.#do_autosave().then(() => {});
    }, this.pgCfg.autosave_delay);
  }

  #cancel_autosave() {
    if (this.autosave_timer === null) return; // No timer, do nothing
    clearTimeout(this.autosave_timer);
    this.autosave_timer = null;
  }

  async #do_autosave() {
    this.autosave_redo = false;
    // cleanup URL encoded info on changes
    let prune_idx = -1;
    let url = document.URL;
    if (url.includes('?')) prune_idx = url.indexOf('?');
    if (url.includes('#')) prune_idx = url.indexOf('#');
    if (prune_idx > -1) {
      url = url.slice(0, prune_idx);
      history.replaceState(undefined, undefined, url);
    }
    // update local storage
    pers_set_code(this.pgCfg, {str:this.get_editor_value(),ext:this.code_ext});
    // process if on-the-fly is set
    if (this.pgCfg.on_the_fly) {
      if (this.cproc.state !== QueryState.READY) {
        this.autosave_redo = true; /* cproc not ready, try again later... */
      } else {
        /* Note that autosave_redo may become 'true' during await */
        await process_code(this);
      }
    }
    this.autosave_timer = null; // Mark timer as done
    if (this.autosave_redo) this.#sched_autosave(); // Reschedule if needed
  }

  /* ---------------------------------------------------------------------- */
  /** Marks and code status (on the editor side) */

  set_code_status(st) {
    let txt = null;
    if (this.cell_data.kind == 'code') {
      switch(st) {
      case 'failed': txt = '&#10008;'; break;
      case 'checked': txt = '&#10004;'; break;
      default: txt = '?';
      }
    } else if (this.cell_data.kind == 'exercise' || this.cell_data.kind == 'exfilterex') {
      switch(st) {
      case 'failed': txt = '&#128576; &#10008;'; break;
      case 'checked': txt = '&#128571; &#10004;'; break;
      default: txt = '&#129300; ?';
      }
    } else if (this.cell_data.kind == 'exfilter') {
      txt = '&#129300; ?';
    }
    if (txt !== null) {
      let sty;
      switch(st) {
      case 'failed': sty = 'lpdoc-runnable-status-failed'; break;
      case 'checked': sty = 'lpdoc-runnable-status-checked'; break;
      default: sty = 'lpdoc-runnable-status-unknown';
      }
      this.set_code_status_(sty, txt);
    }
  }
  set_code_status_(className, text) {
    const e = this.status_el;
    if (e === null) return;
    e.className = 'lpdoc-runnable-statusmark';
    e.classList.add(className);
    e.innerHTML = text;
  }

  /** Post-process warning/error messages after load */ // TODO: generalize
  mark_errs(out, err) {
    let msgs = parse_error_msg(err); // obtain object with parsed errors
    let file = this.curr_mod_path();
    //
    if (this.is_R) {
      let has_errs = false;
      [msgs.errors, msgs.warnings].forEach(m => {
          m.forEach(e => {
            if (e.file !== undefined && e.file !== file) return; // not this file
            if (e.lines === undefined) return; // general error (no lines defined)
            has_errs |= true;
          });
      });
      if (this.cell_data.kind == 'code' || this.cell_data.kind == 'exfilter') { // menu for loading (focused) code
        this.set_code_status(has_errs ? 'failed' : 'checked');
      } else if (this.cell_data.kind == 'exercise') { /* run tests */
        const regex = /Passed: [0-9]* \((?<passed>[0-9\.]*%)\)/;
        let str = out+err;
        const found = str.match(regex);
        if (found !== null && found.groups.passed !== "100.00%") {
          has_errs |= true;
        }
        this.set_code_status(has_errs ? 'failed' : 'checked');
      }
      // show toplevel only on errors // TODO: not for queries!
      this.toplevel_el.className = has_errs ? '' : 'comint-echo-hidden';
      this.toplevel.update_dimensions();
    }
    // Mark warnings and errors in the editor
    // TODO: line numbers needs adjustments! shown code is not the compiled one
    this.editor.set_markers(file, msgs);
  }

  /** Mark source debug info (unmark if info === null or source is not visible) */
  /* Note: based on ciao-debugger.el:ciao-debug-display-line */
  mark_srcdbg_info(info) {
    if (this.editor === null) return; // (print_prompt may call it without an editor)
    if (info !== null && info.src === this.curr_mod_path()) {
      var cl;
      switch(info.port) {
      case '  Call: ': cl = 'ciao-face-debug-call'; break;
      case '  Exit: ': cl = 'ciao-face-debug-exit'; break;
      case '  Redo: ': cl = 'ciao-face-debug-redo'; break;
      case '  Fail: ': cl = 'ciao-face-debug-fail'; break;
      default: cl = 'ciao-face-debug-expansion'; break; // TODO: unknown port
      }
      // see debugger_lib.pl
      // TODO: ciao-face-debug-expansion is used differently
      // TODO: missing ciao-face-debug-breakpoint
      this.editor.mark_srcdbg_info(cl, info);
    } else {
      this.editor.unmark_srcdbg_info();
    }
  }

  /* ---------------------------------------------------------------------- */
  /** Code editor and toplevel process */

  /* Get editor value (text) */
  get_editor_value() {
    return this.editor.get_value();
  }
  /* Set editor value (text) and file extension */
  set_editor_value(code) {
    if (this.code_ext !== code.ext) {
      this.code_ext = code.ext;
      let kind = file_ext_def[code.ext].kind;
      this.editor.set_value(code.str);
      this.editor.set_kind(kind);
    } else {
      this.code_ext = code.ext;
      this.editor.set_value(code.str);
    }
  }

  /* return completed code */
  complete_code(prettify) {
    let code = this.get_editor_value();
    if (this.is_R) {
      if (prettify) {
        let code0 = code;
        code = '';
        if (this.cell_data['preamble'] !== '') {
          code += (this.cell_data['preamble'] + "\n" +
                   "\n% << end of preamble, next is focused code >>\n\n");
        }
        code += code0 + "\n";
        if (this.cell_data['postamble'] !== '') {
          code += ("\n% << end of focused code, next is postamble >>\n\n" +
                   this.cell_data['postamble'] + "\n");
        }
      } else {
        // TODO: add directive to fix line numbers; this hack only works if preamble has one line
        code = (this.cell_data['preamble'] + " " +
                code + "\n" +
                this.cell_data['postamble'] + "\n");
      }
    } else {
      if (this.pgCfg.amend_on_save) {
        // Do not insert module for non .pl files
        if (this.code_ext == '.pl') { 
          /* TODO: horrible hack: add module if none if found, works better than 'user' modules */
          let matches = code.match(/:-\s*module\(/g); // (do not wait for '.')
          if (matches === null) {
            // code = ":- module(_,_,[assertions]). " + code; // assertions needed for acheck, etc.
            code = ":- module(_,_). " + code;
          }
        }
      }
    }
    return code;
  }

  options_exfilter() {
    let opts = this.cell_data['opts'];    
    return opts;
  }

  solution_exercise() {
    let sol = this.cell_data['solution'];    
    return sol;
  }
  curr_mod_name() { // just the name
    if (this.is_R) {
      return this.cell_data.modname;
    } else {
      return guess_mod_name(this.get_editor_value());
    }
  }
  curr_mod_name_ext() { // name and extension
    return this.curr_mod_name()+this.code_ext;
  }
  curr_mod_base() { // full path without extension
    return '/'+this.curr_mod_name();
  }
  curr_mod_path() { // full path with extension
    return '/'+this.curr_mod_name_ext();
  }

  /* ---------------------------------------------------------------------- */

  /** Save current code into the worker file system (possibly "amending it") */
  async upload_code_to_worker() {
    if (!this.cproc.check_not_running()) return;
    let code = this.complete_code(false);
    let file = this.curr_mod_path();
    await this.cproc.w.writeFile(file, code);
  }

  /* ---------------------------------------------------------------------- */
  /** Menu buttons */

  update_layout_sel_button_marks() {
    const filter = (k) => {
      return this.vis.get('layout').enabled_modif(k); 
    };
    if (this.layout_sel_button !== undefined) this.layout_sel_button.update_marks(filter);
  }

  #setup_layout_button(menu_el) {
    const btn =
          new DropdownButton(menu_el,
                             "Change layout",
                             get_icon('layout'),
                             layout_modif_list,
                             value => {
                               this.vis.get('layout').toggle(value);
                               this.update_layout_sel_button_marks();
                               this.update_inner_layout();
                             });
    btn.btn_el.classList.add('menu-button');
    btn.btn_el.style.height = '100%';
    this.layout_sel_button = btn;
    this.update_layout_sel_button_marks();
  }

  // Decided to keep as a separate button (for code/ALD mode change)
  #setup_new_button(menu_el) { 
    const new_list = [];
    for (let ext of get_allowed_file_exts(this.pgCfg)) {
      if (file_ext_def[ext].hide === true) continue;
      new_list.push({ k:'new'+ext,
                      n:'New '+file_ext_def[ext].desc,
                      a:(async(pg) => { return await new_code(pg, ext); })});
    }
    if (new_list.length > 0) {
      if (new_list.length == 1) { // No dropdown, just one button
        const el = btn('menu-button', new_list[0].n, "New", () => {
          new_list[0].a(this).then(() => {}); // TODO: use "async () => { ... }" instead?
        });
        menu_el.appendChild(el);
      } else { // Dropdown
        // TODO: refactor
        const new_button =
              btn_dropdown_act(this, menu_el,
                               "New",
                               elem_from_str("<span>New</span>"),
                               new_list);
        new_button.btn_el.classList.add('menu-button');
        new_button.btn_el.style.height = '100%';
      }
    }
  };

  #setup_file_button(menu_el) { 
    const new_list = [];
    new_list.push({ k:'open',
                    n:'Open local file',
                    a:(async(pg) => { return await this.upload_file(); })
                  });
    new_list.push({ k:'save',
                    n:'Save locally (download)',
                    a:(async(pg) => { return await this.download_file() })
                  });
    const new_button =
          btn_dropdown_act(this, menu_el,
                           "Load, save, or reset files in editor area",
                           elem_from_str("<span>File</span>"),
                           new_list);
    new_button.btn_el.classList.add('menu-button');
    new_button.btn_el.style.height = '100%';
  };

  upload_file() {
    const allowed_exts = get_allowed_file_exts(this.pgCfg);
    const file_el = document.createElement('input');
    file_el.type = 'file';
    file_el.accept = allowed_exts.join(',');
    file_el.click(); // trigger input (allowed because it's triggered by a user gesture)
    file_el.addEventListener('change', (e) => { handle_file_upload(e, file_el, this); });
  }
  download_file() {
    const name = this.curr_mod_name_ext(); // obtain name with extension
    const file = new Blob([this.get_editor_value()], { type: 'text/plain' });
    const url = URL.createObjectURL(file);
    const a_el = document.createElement('a');
    a_el.href = url;
    a_el.download = name;
    a_el.click();
    URL.revokeObjectURL(url);
  }

  #setup_examples_button(menu_el) {
    const examples_button =
          new DropdownButton(menu_el, // Examples menu
                             "Select and load examples",
                             elem_from_str("<span>Examples</span>"),
                             this.pgCfg.example_list,
                             value => {
                               (async() => {
                                 await open_example(this, value);
                               })();
                             });
    examples_button.btn_el.classList.add('menu-button');
    examples_button.btn_el.style.height = '100%';
  }

  #setup_load_button(menu_el) { // Load into top level or generate ALD
    const el = btn('menu-button',
                   "Load code into top level or preview document (C-c l)",
                   "Load &nbsp;&#9654",
                   () => {
                     load_code(this).then(() => {}); // TODO: use "async () => { ... }" instead?
    }); 
    this.load_button_el=el; // Saved for later modification 
    menu_el.appendChild(el);
  }

  #setup_ext_mode_button(menu_el) { // Toggle presentation mode
    const el = btn('menu-button',
                   "Current mode: code or notebook. Can be toggled.",  // "Keybinding?",
                   // file_ext_def[this.code_ext].button_text, 
                   `<div style="min-width: 30px"></div>`, // Will be  filled in later 
                   () => { 
      toggle_file_ext_mode(this).then(() => {}); // TODO: use "async () => { ... }" instead?
    });
    this.ext_mode_button_el=el; // Saved for later modification 
    menu_el.appendChild(el);
  }
  
  #setup_doc_button(menu_el) { // Preview documentation
    const el = btn('menu-button',
                   "Generate and preview documentation (C-c D)",
                   "&#128214;",
                   () => {
                     gen_doc_preview(this).then(() => {})} ); // TODO: use "async () => { ... }" instead?
    menu_el.appendChild(el);
  }

  #setup_debug_button(menu_el) { // Debug
    const el = btn('menu-button',
                   "Debug code (C-c d)",
                   "&#x1F41E;",
                   () => {
                     debug(this).then(() => {})} ); // TODO: use "async () => { ... }" instead?
    this.debug_button_el=el; // Saved for later modification 
    menu_el.appendChild(el);
  }

  #setup_toggle_on_the_fly_button(menu_el) { // Toggle on-the-fly mode
    const el = btn('menu-button',
                   "Toggle on-the-fly mode for preview, analysis, etc.",  // "Keybinding?",
                   '<span style="opacity: 0.5">&#x26A1;</span>', // See toggle code also
                   () => {
                     toggle_on_the_fly(this).then(() => {}); // TODO: use "async () => { ... }" instead?
    }); 
    this.on_the_fly_button_el=el; // Saved for later modification 
    menu_el.appendChild(el);
  }
  
  #setup_toggle_presentation_button(menu_el) { // Toggle presentation mode
    const el = btn('menu-button',
                   "Toggle presentation (full screen) mode for preview area",  // "Keybinding?",
                   "&#x26F6;", () => {
      toggle_presentation(this).then(() => {}); // TODO: use "async () => { ... }" instead?
    }); 
    menu_el.appendChild(el);
  }
  
  #setup_advanced_buttons(menu_el) {
    const adv_list = [];
    if (this.pgCfg.has_run_tests_button) {
      adv_list.push({ k:'test', n:'Run tests (if any) (C-c u)', a:run_tests });
    }
    if (this.pgCfg.has_acheck_button) {
      adv_list.push({ k:'acheck', n:'Analyze and check assertions (C-c V)', a:acheck });
    }
    if (this.pgCfg.has_acheck_button) {
      adv_list.push({ k:'acheck_output', n:'Analyze and check assertions (w/output)', a:acheck_output });
    }
    if (this.pgCfg.has_spec_button) {
      adv_list.push({ k:'spec', n:'Specialize code (C-c O)', a:spec_preview });
    }
    if (this.pgCfg.has_browse_analysis_opts_button) {
      adv_list.push({ k:'browse_analysis_opts', n:'Browse analysis/checking/optimizing options', a:browse_analysis_opts });
    }
    if (adv_list.length > 0) {
      const adv_button =
            btn_dropdown_act(this, menu_el,
                             "Advanced actions",
                             elem_from_str("<span>&#x22EF;</span>"), // "More..."
                             adv_list);
      adv_button.btn_el.classList.add('menu-button');
      adv_button.btn_el.style.height = '100%';
      this.more_button_el = adv_button.btn_el; // Saved for later modification 
    }
  }
  
  #setup_lptp_buttons(menu_el) {
    const lptp_list = [];
    lptp_list.push({
      k: 'send_buffer',
      n: 'Send buffer (C-c i s)',
      a: async () => await send_lptp_buffer(this)});

    lptp_list.push({
      k: 'get_buffer',
      n: 'Get buffer (C-c i g)',
      a: async () => await get_lptp_buffer(this)});

    lptp_list.push({
      k: 'get_send_buffer',
      n: 'Send and get buffer (C-c i G)',
      a: async () => {
	await send_lptp_buffer(this);
	await get_lptp_buffer(this);
      }
    });

    const tactic_list = [
      ['auto', 'C-c i a'],
      ['case', 'C-c i c'],
      ['comp', 'C-c i o'],
      ['elim', 'C-c i e'],
      ['ex', 'C-c i x'],
      ['fact', 'C-c i f'],
      ['ind', 'C-c i i'],
      ['indqf', 'C-c q'],
      ['tot', 'C-c i t'],
      ['unfold', 'C-c u']];

    tactic_list.map(([tactic_name, shortcut]) => {
      lptp_list.push({
        k: `tactic_${tactic_name}`,
        n: `Tactic: ${tactic_name} (${shortcut})`,
        a: async () => await lptp_tactic(this, tactic_name)});
    });

    lptp_list.push({
      k: 'lptp_debug',
      n: 'Debug (C-c i b)',
      a: async () => await lptp_tactic(this, 'debug')});


    lptp_list.push({
      k: 'list_facts',
      n: 'List facts (C-c i l)',
      a: async () => await list_lptp_facts(this)});

    lptp_list.push({
      k: 'print_definition',
      n: 'Print definition (C-c i d)',
      a: async () => await print_lptp_definition(this)});

    lptp_list.push({
      k: 'lptp_mark',
      n: 'Mark formula (C-c i m)',
      a: async () => await lptp_mark(this)});

    const lptp_button = btn_dropdown_act(this, menu_el,
                                         "Lptp actions",
                                         elem_from_str("<span>LPTP</span>"),
                                         lptp_list);
    lptp_button.btn_el.classList.add('menu-button');
    lptp_button.btn_el.style.height = '100%';
    this.lptp_button_el = lptp_button.btn_el; // Saved for later modification 
  }

  #setup_share_button(menu_el) {
    const el = elem_cn('button', 'menu-button');
    el.title = 'Copy a link to this code or document';
    el.appendChild(get_icon('share'));
    const msg_el = document.createTextNode("Share!");
    el.appendChild(msg_el);
    el.onclick = () => { handle_share(el, msg_el, this); };
    menu_el.appendChild(el);
  }

  update_menu_buttons() {
    if (this.ext_mode_button_el !== undefined) this.ext_mode_button_el.innerHTML = `<div style="min-width: 30px">`+file_ext_def[this.code_ext].button_text+'</div>'; 
    if (this.load_button_el !== undefined) this.load_button_el.style.display = ( file_ext_def[this.code_ext].load_menu ? 'block' : 'none' );
    if (this.debug_button_el !== undefined) this.debug_button_el.style.display = ( file_ext_def[this.code_ext].debug_menu ? 'block' : 'none' );
    if (this.more_button_el !== undefined) this.more_button_el.style.display = ( file_ext_def[this.code_ext].more_menu ? 'block' : 'none' );
    if (this.on_the_fly_button_el !== undefined) this.on_the_fly_button_el.innerHTML = (this.pgCfg.on_the_fly ? '&#x26A1;' : '<span style="opacity: 0.5">&#x26A1;</span>');
  }

  /* ---------------------------------------------------------------------- */
  /** Execute command on response view */

  async with_response(f) { /* pre: this.is_R */
    if (!this.cproc.check_not_running()) return;
    await this.enable_response({noprompt: true, autoresize:true, noshrink: true});
    await this.cproc.ensure_started(this.toplevel);
    //
    this.toplevel.clear_output(); // TODO: customize?
    await f(this);
    this.toplevel.reveal_first(); // TODO: customize?
    if (!this.pgCfg.runnable_keep_alive) {
      this.cproc.shutdown();
    }
  }

  /* ---------------------------------------------------------------------- */

  /** Redirect to playground (open playground in new tab from URL) */
  load_in_playground() { /* pre: this.is_R */ // TODO: treat .md case (and do not complete code? introduces :- module)
    let code = this.complete_code(true);
    window.open(playground_URL(code, '.pl')); // open playground in new tab
  }

  /** Set up cell with dynamic preview (TODO: experimental) */
  async setup_dynpreview(opts) { /* pre: this.is_R */
    let pgset = this.pgset;
    let render_pred = opts.render_pred;
    // extend dependencies and init queries (before start)
    if (opts.depends !== undefined) this.cproc.push_depends(opts.depends);
    if (opts.on_init !== undefined) this.cproc.push_on_init(opts.on_init);
    // assign an identifier in pgset // TODO: hardwired!
    if (pgset.dynpreview === undefined) {
      pgset.dynpreview = [];
    }
    const id = pgset.dynpreview.length;
    pgset.dynpreview.push(this);
    // create a dummy console comint (this cell has no output)
    this.toplevel = new ConsoleComint(this);
    // start
    await this.cproc.ensure_started(this.toplevel);
    // 
    window.dynpreview_visit = (id,state) => { // push state and render
      const pg = pgset.dynpreview[id];
      if (opts.state_hash === true) {
        push_state_to_hash(state);
        window.scrollTo(0,0);
      }
      (async() => {
        await pg.cproc.muted_query_dumpout(render_pred+"("+id+",'"+state+"')"); // TODO: json state?
      })();
    };
    window.dynpreview_render = (id,html) => {
      const pg = pgset.dynpreview[id];
      pg.show_preview(true);
      pg.update_inner_layout();
      const e = pg.preview_el;
      e.innerHTML=html;
    }
    // 
    const read_state_and_render = async() => {
      const state = (opts.state_hash === true) ? read_state_from_hash() : '';
      await this.cproc.muted_query_dumpout(render_pred+"("+id+",'"+state+"')"); // TODO: json state?
    };
    await read_state_and_render();
    if (opts.state_hash === true) addEventListener('hashchange', event => { read_state_and_render().then(() => {}); });
  }
}

function get_file_extension(filename) {
  return filename.match(/\.[0-9a-z]+$/i)[0]; 
}

function handle_file_upload(event, file_el, pg) {
  const file = event.target.files[0];
  if (!file) {
    alert('No file uploaded');
    return;
  }
  let ext = get_file_extension(file.name);
  let allowed_exts = get_allowed_file_exts(pg.pgCfg);
  if (!allowed_exts.includes(ext)) { // none of the accepted extensions
    alert('Invalid file type (expected: '+allowed_exts.join(', ')+').');
    file_el.value = '';  // reset file input
    return false;
  } else {
    const reader = new FileReader();
    reader.onload = (event) => {
      (async() => {
        await pg.set_code_and_process({str:event.target.result, ext:ext});
      })();
    };
    reader.readAsText(file);
  }
}

/** Mini-playgrond (e.g, for website) */
function setup_mini_pg(pg, el) {
  pg.pgCfg = Object.assign({...pg.pgCfg}, miniPlaygroundCfg);
  el.style.width='100%';
  el.style.height='250px'; // TODO: customize?
  el.style.display='flex';
  el.style.marginBottom='24px';
  el.style.flexFlow='column';
}

/** Open example */
async function open_example(pg, path) {
  var pathsplit = path.split('/');
  var b = pathsplit[0]; // assume first component is bundle
  var rest = pathsplit.slice(1).join('/'); // rest of path
  // load bundle if needed (also reload_bundleregs/0)
  await pg.cproc.push_depends([b]);
  try {
    let srcdir = await pg.cproc.w.get_bundle_srcdir(b);
    path = srcdir + '/' + rest;
    console.log(`{reading file '${path}'}`);
    var str = await pg.cproc.w.readFile(path);
    let ext = get_file_extension(path);
    // console.log(`{getting code '${str}' '${ext}'}`);
    await pg.set_code_and_process({str:str, ext:ext});
  } catch (e) {
    console.log(`{error: could not open example: ${e}}`);
    // return null;
  }
}

/* Handle share */
function handle_share(btn_el, msg_el, pg) {
  let value = pg.get_editor_value();
  let ext = pg.code_ext;
  navigator.clipboard.writeText(code_to_URL(value,ext)).then(() => { // success
    let prev = msg_el.textContent;
    msg_el.textContent = 'Copied!';
    btn_el.style.color = 'var(--face-checked-assrt)';
    setTimeout(() => {
      msg_el.textContent = prev;
      btn_el.style.color = null;
    }, 500);
  }).catch(err => {
    console.log('Something went wrong', err);
  });
}

/* Initial editor code (splash, URI encoded, URL from a CDN, from storage, etc.) */
async function initial_editor_code(pgCfg) {
  let code;
  // Extract from URL link
  code = await code_from_URL_link();
  if (code !== null) return code;
  // Extract from URL
  code = code_from_URL();
  if (code !== null) return code;
  // Try from persistent store
  code = pers_get_code(pgCfg);
  if (code !== null) return code;
  // Just show splash code
  return get_splash_code_for_ext(pgCfg, splash_default_ext(pgCfg));
}

// (for dynpreview)
function push_state_to_hash(state) {
  const hash = (state === '') ? window.location.pathname : "#"+encodeURI(state);
  history.pushState('','',hash);
}

// (for dynpreview)
function read_state_from_hash() {
  let state='';
  if (document.URL.includes('#')) { // Code in the URL
    let value = document.location.hash.substring(1); // strip off leading #
    state = decodeURI(value); // decode URI
  }
  return state;
}

/** Toggle presentation mode */
async function toggle_presentation(pg) {
  if (pg.is_R) return; /* (do nothing) */
  pg.vis.set('presentation', !pg.vis.get('presentation'));
  pg.update_inner_layout();
}

/** Toggle file extension (.pl/.md) mode */
async function toggle_file_ext_mode(pg) {
  if (pg.is_R) return; /* (do nothing) */
  pg.code_ext = ( file_ext_is_doc(pg.code_ext) ? '.pl' : '.md' ); 
  pg.update_menu_buttons();
}

/** Toggle on-the-fly mode (and button label) */
async function toggle_on_the_fly(pg) {
  pg.pgCfg.on_the_fly = !pg.pgCfg.on_the_fly;
  pg.update_menu_buttons();
}

// ---------------------------------------------------------------------------
// * Update editor theme

// Called when theme changes
{ 
  const prev = update_theme_hook;
  update_theme_hook = () => { // TODO: better way to extend hooks?
    prev(); 
    editor_update_theme();
  }
};

// ---------------------------------------------------------------------------
// * Playground actions on code  - new, process, actions (load, gen_doc, exfilter, ...)

/** New code for the given extension (reset editor contents) */
async function new_code(pg, ext) {
  let text = "Discard the current editor contents?";
  // TODO: use custom dialog, make default to cancel
  if (confirm(text) == true) {
    let code = get_splash_code_for_ext(pg.pgCfg, ext);
    await pg.set_code_and_process(code); 
  }  
}

/** Process code (load, doc, etc.) */
async function process_code(pg) {
  let pmuted = null;
  if (pg.pgCfg.on_the_fly) pmuted = pg.cproc.set_muted(true); // TODO: mute in on_the_fly; make it optional?
  switch(pg.get_auto_action()) {
  case 'load': await load_code(pg); break;
  case 'doc': await gen_doc_preview(pg); break;
  case 'test': await run_tests(pg); break;
  case 'acheck': await acheck(pg); break;
  case 'acheck_output': await acheck_output(pg); break;
  case 'spec': await spec_preview(pg); break;
  case 'exfilter': await run_exfilter(pg); break;
  case 'exfilter_exercise': await run_exfilter_exercise(pg); break;
  }
  if (pmuted !== null) pg.cproc.muted = pmuted;
}

/** Load code */
async function load_code(pg) {
  let q;
  if (file_ext_is_doc(pg.code_ext)) {
    // .md/.lpdoc are not loaded into top level:
    // instead generate doc and preview
    gen_doc_preview(pg);
  } else {
    if (!pg.cproc.muted) {
      pg.show_toplevel(true);
      pg.update_inner_layout();
    }
    const mod = pg.curr_mod_path();
    if (toplevelCfg.statistics) console.log(`{loading '${mod}'}`);
    if (toplevelCfg.custom_load_query !== undefined) {
      q = toplevelCfg.custom_load_query(mod);
    } else {
      q = "use_module('" + mod + "')";
    }
    await pg.toplevel.do_query(q, {msg:'Loading'}); // TODO: this last 'await' here should not be needed but it does not work otherwise... WHY? A problem with CiaoPromiseProxy? (JFMC)
    pg.set_auto_action('load');
  }
}

/** Exfilter */
async function run_exfilter(pg) {
  const mod = pg.curr_mod_path();
  const modbase = pg.curr_mod_base();
  // Heuristic to determine if the filtering output comes from a ciaopp output file or from top-level messages
  const opts = pg.options_exfilter().split(",");
  if (opts.includes("V") === true) {
    if (opts.includes("output=on") === true) {
      var kind = file_ext_def['.pl'].kind;
    } else {
      var kind = 'toplevel';
    }
  } else {
    var kind = file_ext_def['.pl'].kind;
  }
  await pg.toplevel.do_query("filter_analyze(\"" + mod + "\",\"" + opts +"\")", {msg:'Analyzing'});
  var str = await pg.cproc.w.readFile(modbase+'.txt');
  if (str !== null) {
    str = str.trim(); // TODO: this should be done by exfilter
    await show_text_highlight(pg, str, kind);
  } 
  pg.set_auto_action('exfilter');
}

/** Exfilter exercise */
async function run_exfilter_exercise(pg) {
  const mod = pg.curr_mod_path();
  const modbase = pg.curr_mod_base();
  // Heuristic to determine if the filtering output comes from a ciaopp output file or from top-level messages
  const opts = pg.options_exfilter().split(",");
  if (opts.includes("solution=errors") === true) {
    var kind = 'toplevel';
  } else {
    var kind = file_ext_def['.pl'].kind;
  }
  const sol = pg.solution_exercise();
  await pg.toplevel.do_query("filter_analyze_exercise_mode(\"" + mod + "\",\"" + sol + "\",\""+ opts + "\")", {msg:'Analyzing answer'});
  var str = await pg.cproc.w.readFile(modbase+'.txt');
  if (str !== null) {
    str = str.trim(); // TODO: this should be done by exfilter
    let msgs = parse_error_msg("{Reading\n"+str+"\n}"); // note: surround by {Reading ... } so that the parser understands it
    await show_text_highlight(pg, str, kind);
    if (str == "Correct"){
      pg.set_code_status('checked');
      var preview = pg.preview_el; 
      preview.replaceChildren();
    } else if (str == "Incorrect") {
      pg.set_code_status('failed');
      var preview = pg.preview_el; 
      preview.replaceChildren();
    } else if (msgs.errors.length !== 0 || msgs.warnings.length !== 0) {
      pg.set_code_status('failed');
    } else if (str.match(/:\-\s*false/) !== null) {
      pg.set_code_status('failed');
    } else {
      pg.set_code_status('checked');
    }            
  }
  pg.set_auto_action('exfilter_exercise');
}

/** Gen doc and preview */
async function gen_doc_preview(pg) {
  await gen_doc(pg);
  await doc_preview(pg);
  pg.set_auto_action('doc');
}

/** Run optimizations and preview */
async function spec_preview(pg) {
  await opt_mod(pg);
  await preview_co(pg);
  pg.set_auto_action('spec');
}

/** Run tests */
async function run_tests(pg) {
  if (!pg.cproc.muted) {
    pg.show_toplevel(true);
    pg.update_inner_layout();
  }
  const mod = pg.curr_mod_path();
  // await pg.upload_code_to_worker(); // TODO: do not load, just save to fs
  const pmuted = pg.cproc.set_muted(true); // TODO: mute in on_the_fly; make it optional?
  await pg.toplevel.do_query("use_module('" + mod + "')", {no_treat_outerr: true}); // TODO: this should not be needed, but sometimes (when there is some ':- test') code is not available anymore after run_tests_in_module/1, why? // (do a muted query so that code status is not affected, run_tests_in_modules does use module also
  pg.cproc.muted = pmuted;
  // await pg.cproc.muted_query_dumpout("use_module(library(unittest))"); // (done by special_query.depends)
  await pg.toplevel.do_query("run_tests_in_module('" + mod + "')", {msg:'Testing'}); // TODO: this last 'await' here should not be needed but it does not work otherwise... WHY? A problem with CiaoPromiseProxy? (JFMC)
  pg.set_auto_action('test');
}

/** Debug code (it loads the libraries needed and starts the debugger) */
async function debug(pg) {
  if (!pg.cproc.muted) {
    pg.show_toplevel(true);
    pg.update_inner_layout();
  }
  const mod = pg.curr_mod_path();
  const modname = pg.curr_mod_name();
  // TODO: make debugger work in playground
  if (!pg.debugging) {
    await pg.toplevel.do_query("display_debugged", {});
    await pg.toplevel.do_query("debug_module_source('" + modname + "')", {});
    await pg.toplevel.do_query("trace", {});
    pg.debugging = true; // change debugging status
  } else {
    await pg.toplevel.do_query("nodebug_module('" + modname + "')", {});
    await pg.toplevel.do_query("nodebug", {});
    pg.debugging = false; // change debugging status
  }
  await pg.toplevel.do_query("use_module('" + mod + "')", {});
}

/** Generate documentation */
/* (requires 'lpdoc' bundle) */
async function gen_doc(pg) {
  const filename = pg.curr_mod_name_ext();
  // await pg.cproc.muted_query_dumpout("clean_mods(['"+modbase+"'])"); // (timestamps do not have the right resolution)
  await pg.toplevel.do_query("doc_cmd('"+filename+"', [], clean(intermediate))", {}); // (clean mod above is not enough)
  if (file_ext_is_doc(pg.code_ext)) { 
    // TODO: fix bug in LPdoc!
    // Deleting file.pl in case there is file.pl and file.md
    let plfile = pg.curr_mod_name()+'.pl';
    await pg.toplevel.do_query("use_module(library(system_extra))", {}); 
    await pg.toplevel.do_query("del_file_nofail('"+plfile+"')", {}); // continuation of above
  }
  await pg.toplevel.do_query("doc_cmd('"+filename+"', [], gen(html))", {msg:'Generating documentation'});
  pg.set_auto_action('doc');
}

/** Analyze and check assertions */
/* (requires 'ciaopp' bundle) */
async function acheck(pg) {
  await discard_preview(pg);
  if (!pg.cproc.muted) {
    pg.show_toplevel(true);
    pg.update_inner_layout();
  }
  const modbase = pg.curr_mod_base();
  if (pg.get_auto_action() !== 'acheck') await pg.toplevel.do_query("set_menu_flag(ana, output, off)", {});
  await pg.toplevel.do_query("auto_check_assert('"+modbase+"')", {msg:'Checking assertions'});
  pg.set_auto_action('acheck');
}
async function acheck_output(pg) { // (shows output, which can be slower)
  const modbase = pg.curr_mod_base();
  if (pg.get_auto_action() !== 'acheck_output') await pg.toplevel.do_query("set_menu_flag(ana, output, on)", {});
  await pg.toplevel.do_query("auto_check_assert('"+modbase+"')", {msg:'Checking assertions'});
  await preview_co(pg);
  pg.set_auto_action('acheck_output');
}

/** Show the CiaoPP flags interactive menu */
/* (requires 'ciaopp' bundle) */
async function browse_analysis_opts(pg) {
  // Translate the query so the special query table is checked
  // in this cases (needed for loading ciaopp bundle and importing it).
  // TODO: Better way?
  const tr = await pg.cproc.trans_query('write_ciaopp_menu_json');
  const menudef = await pg.cproc.muted_query_getout(tr.q);
  const menu = new Menu('Preprocessor Option Browser', JSON.parse(menudef), () => {
    pg.cproc.muted_query_dumpout(menu.get_query_for_loading_menu());
  });

  show_ciaopp_menu(pg, menu.elem);
}

/** Show CiaoPP generated menu HTML in preview */
// // (Version using preview_el)
// async function show_ciaopp_menu(pg, m) {
//   pg.show_preview('tall');
//   var preview = pg.preview_el;
//   preview.replaceChildren();
//   preview.style.fontFamily = null;
//   preview.style.overflow = 'auto';
//   preview.appendChild(m);
//   pg.update_inner_layout();
// }
async function show_ciaopp_menu(pg, menu_el) {
  let base_el = pg.pgset.base_el;
  let modal = new Modal();
  modal.alloc(base_el);
  modal.set_wide();
  const m = modal.get_elm();
  m.replaceChildren();
  // Big close button
  const el = btn('menu-button', "Close", "Close", () => {
    modal.close();
  });
  const btn_w = elem_cn('div', 'big-button-wrapper');
  btn_w.appendChild(el);
  menu_el.appendChild(btn_w);
  //
  m.appendChild(menu_el);
  modal.set_visible(true);
}

/** Optimize (spec) module */
/* (requires 'ciaopp' bundle) */
async function opt_mod(pg) {
  const modbase = pg.curr_mod_base();
  await pg.toplevel.do_query("auto_optimize('"+modbase+"')", {msg:'Specializing'});
  pg.set_auto_action('spec');
}

/** Preview the documentation generated for the current module */
async function doc_preview(pg) {
  const modbase = pg.curr_mod_base();
  var str = await pg.cproc.w.readFile(modbase+'.html/index.html');
  if (str !== null) {
    const d = extract_lpdoc_main_el(str);
    await show_lpdoc_html(pg, d);
  }
}
// Extract main div element from the html string of an LPdoc .html file
function extract_lpdoc_main_el(str) {
  let d = document.createElement('template');
  d.innerHTML = str;
  d = d.content.querySelector('.lpdoc-main');
  if (d === null) return d; // (no output)
  // Add a special lpdoc-nav (for preview_doc.prepare)
  let nav_el = elem_cn('div', 'lpdoc-nav');
  nav_el.classList.add('lpdoc-sidebar');
  nav_el.style.cssFloat = 'right';
  nav_el.style.padding = '8px 0px 0px 0px';
  d.prepend(nav_el);
  return d;
}
// Show LPdoc generated element d in preview (experimental: update lpdoc runnable code)
// TODO: mathjax (if enabled)
async function show_lpdoc_html(pg, d) {
  pg.show_preview('tall'); // use tall preview
  var preview = pg.preview_el; // TODO: do not change style dynamically for this preview_el
  //preview.className = 'preview-container-lpdoc';
  preview.replaceChildren();
  preview.style.fontFamily = null;
  preview.style.overflowX = 'auto';
  preview.style.overflowY = 'auto';
  preview.style.outline = 'none'; // no focus outline
  preview.setAttribute('tabindex', '-1'); // make it focusable (for key input)
  //
  if (d === null) return; // (wrong output!)
  //
  preview.appendChild(d); // lpdoc-main div
  // save preview_doc position/mode
  let prev_pos = null;
  if (pg.preview_doc) prev_pos = pg.preview_doc.get_pos();
  /* prepare preview doc, enable code runnable in the lpdoc output if needed */
  let preview_doc = new SlideShow(); // TODO: make SlideShow view optional
  pg.preview_doc = preview_doc;
  preview_doc.prepare(preview);
  if (prev_pos) preview_doc.restore_pos(prev_pos);
  preview_doc.set_slide_mode(preview_doc.enabled); // (changes it if needed)
  let preview_pgset = new PGSet(); // TODO: reuse!?
  preview_pgset.attach_main_doc(preview_doc);
  await preview_pgset.setup_runnable(preview);
  if (preview_pgset.cproc.state === QueryState.READY) { // TODO: schedule run?
    await preview_pgset.reset_all_code();
  }
  pg.update_inner_layout();
}

/** Preview _co.pl contents for the current module */
async function preview_co(pg) {
  // TODO: only if code_ext==='.pl'
  const modbase = pg.curr_mod_base();
  var str = await pg.cproc.w.readFile(modbase+'_co.pl');
  if (str !== null) {
    let kind = file_ext_def['.pl'].kind;
    await show_text_preview(pg, str, kind);
  }
}

/** Show raw (no highlighted) text in a PRE environment */
async function show_text(pg, d) {
  pg.show_preview('tall'); // use tall preview
  var preview = pg.preview_el; // TODO: do not change style dynamically for this preview_el
  //preview.className = 'preview-container-lpdoc';
  preview.replaceChildren();
  preview.style.overflowX = 'hidden';
  preview.style.overflowY = 'hidden';
  var el = document.createElement('pre');
  el.innerText = d;
  // TODO: use read-only editor container (with syntax highligh)
  el.className = 'lpdoc-codeblock';
  el.style.height = '100%';
  el.style.margin = '0px';
  el.style.border = null;
  el.style.borderRadius = null;
  el.style.overflowX = null;
  el.style.overflow = 'auto';
  preview.appendChild(el);
  pg.update_inner_layout();
}

/** Discard preview contents and hide */
async function discard_preview(pg) {
  pg.show_preview(false); 
  var preview = pg.preview_el; 
  preview.replaceChildren();
}

/** Show highlighted text in a read-only editor view (playground) */
async function show_text_preview(pg, d, kind) {
  pg.show_preview('tall'); 
  var preview = pg.preview_el; 
  preview.replaceChildren();
  preview.style.overflowX = 'hidden';
  preview.style.overflowY = 'hidden';
  var el = elem_cn('div', 'editor-container'); //document.createElement('pre');
  el.style.height = '100%'; // (Needed because this is inside another div) TODO: better way?
  preview.appendChild(el);
  pg.previewEd = create_pg_editor(el, d, kind, {});
  pg.previewEd.set_read_only(true);
  pg.previewEd.set_line_numbers(false);
  pg.update_inner_layout();
}

/** Show highlighted text in a read-only editor view (for lpdoc-runnable) */
async function show_text_highlight(pg, d, kind) {
  pg.show_preview('tall'); // use tall preview
  var preview = pg.preview_el; 
  preview.replaceChildren();
  preview.style.overflowX = 'hidden';
  preview.style.overflowY = 'hidden';
  var el = elem_cn('pre', 'lpdoc-codeblock');
  el.style.height = '100%';
  el.style.marginTop = '5px';
  el.style.border = null;
  el.style.borderRadius = null;
  el.style.overflowX = null;
  el.style.overflow = 'hidden'; // TODO: better way?
  preview.appendChild(el);
  var previewEd = create_pg_editor(el, d, kind, {autoresize: true}); // TODO: the reference to this editor is lost
  previewEd.set_read_only(true);
  previewEd.set_line_numbers(false);
  pg.update_inner_layout();
}

// ---------------------------------------------------------------------------
// * Guess module name

/**
 * Guess the module name from the code (no extension, no path). It
 * checks if the module name is available in a module declaration
 * `:- module(name,...).`, otherwise uses `draft` as module name.
 *
 * @param {string} code - String containing the Prolog code to scan.
 * @returns {string} - Name of the file
 */
function guess_mod_name(code) {
  let matches = code.match(/:-\s*module\(([a-z][_a-zA-Z0-9]*)\s*,/); // (do not wait for '.')
  if (matches != null && matches.length == 2) {
    return matches[1];
  } else {
    return 'draft'; // default module name
  }
}

// ---------------------------------------------------------------------------
// * Encode/decode code as URL, fetch from CDN

/**
 * New URL for opening the playground with some code
 */
function playground_URL(code,ext) {
  let url = urlPREFIX+'/playground/index.html'; /* no vers */
  return url + '?code=' + encodeURIComponent(code) + '&ext=' + ext;
}

/**
 * Encode the code and extension given as a playground URL
 * @returns {string} Shareable link including the code in the editor
 * and the extension. 
 */
// TODO: Encode also the window layout?
function code_to_URL(value,ext) {
  let url = document.URL;
  if (url.includes('#') || url.includes('?code=')) {
    // url = url.slice(0, url.indexOf('#'));
    return url; // content hasn't changed
  }
  return url + '?code=' + encodeURIComponent(value) + '&ext=' + ext;
}

/**
 * Extract the code given in the current document URL (null otherwise)
 */
function code_from_URL() {  
  // Code in '#' [deprecated] (legacy links)
  if (document.URL.includes('#')) {
    return decodeURI(document.location.hash.substring(1)); // (decode, without #)
  }
  // Code in 'code' param and 'ext' params
  let doc_url = document.location.search; 
  const params = new URLSearchParams(doc_url);
  let str = params.get("code");
  if (str === null) return null; // Code not in URL
  let ext = params.get("ext");
  if (ext === null) ext = '.pl'; // default .pl
  return {str:str,ext:ext,origin:'URL'};
}

/**
 * Extract code from URL link
 */

// TODO: add load from gitlab
const github_hash = '#https://github.com/';
const local_hash = '#/';

async function code_from_URL_link() {
  let hash = document.location.hash;
  if (hash.startsWith(github_hash)) {
    // Load from github
    // Example:
    //   https://ciao-lang.org/playground/#https://github.com/ciao-lang/ciaopp/blob/master/examples/verifly/ann.pl
    //   https://ciao-lang.org/playground/#https://github.com/ciao-lang/lpdoc/blob/master/examples/factorial_peano_iso.md
    let p = hash.substring(github_hash.length);
    let str = await fetch_from_github(p);
    let ext = get_file_extension(hash); // (this should end with the extension)
    return {str:str,ext:ext,origin:'URL_link'};
  } else if (hash.startsWith(local_hash)) {
    // Load from a URI local to the playground server
    //   https://ciao-lang.org/playground/#/tmp/foo.md
    let p = hash.substring(local_hash.length);
    let str = await fetch_from_local(p);
    let ext = get_file_extension(hash); // (this should end with the extension)
    return {str:str,ext:ext,origin:'URL_link'};
  } else {
    return null;
  }
}

async function fetch_from_github(p) {
  /* Use https://cdn.jsdelivr.net CDN to fetch the file */
  //let url = 'https://github.com/USER/REPO/blob/BRANCH/RELPATH';
  let gh_user = p.substring(0, p.indexOf('/')); p = p.substring(p.indexOf('/')+1);
  let gh_repo = p.substring(0, p.indexOf('/blob/')); p = p.substring(p.indexOf('/blob/')+'/blob/'.length);
  let gh_branch = p.substring(0, p.indexOf('/')); p = p.substring(p.indexOf('/')+1);
  let gh_relpath = p;
  let url = `https://cdn.jsdelivr.net/gh/${gh_user}/${gh_repo}@${gh_branch}/${gh_relpath}`;
  let res = await fetch(url);
  let txt = await res.text();
  return txt;
}

async function fetch_from_local(p) {
  let url = `/${p}`;
  let res = await fetch(url);
  let txt = await res.text();
  return txt;
}

// ---------------------------------------------------------------------------
// * Common playground bindings 

function add_playground_bindings(editor, pg) {
  // Save file to local file-system (C-x C-s)
  editor.set_key("C-x C-s", () => { pg.download_file(); }); // like 'save' button
  // Go to the other window (C-x o)
  editor.set_key("C-x o", () => { pg.change_focus(); });
  // Save and run code (C-c l)
  if (pg.pgCfg.has_load_button) {
    editor.set_key("C-c l", () => { load_code(pg).then(() => {}); }); // TODO: use .catch(err => console.log(err))
  }
  // Run tests in current module (C-c u)
  if (pg.pgCfg.has_run_tests_button) {
    editor.set_key("C-c u", () => { run_tests(pg).then(() => {}); });
  }
  // Debug source code (C-c d)
  if (pg.pgCfg.has_debug_button) { // debug
    editor.set_key("C-c d", () => { debug(pg).then(() => {}); });
  }
  // Document source code (C-c D)
  if (pg.pgCfg.has_doc_button) {
    editor.set_key("C-c S-d", () => { gen_doc_preview(pg).then(() => {}); });
  }
  // Analyze and check assertions (C-c V)
  if (pg.pgCfg.has_acheck_button) {
    editor.set_key("C-c S-v", () => { acheck(pg).then(() => {}); });
  }
  
  if (pg.pgCfg.has_lptp_button) add_lptp_bindings(editor, pg); 
}

function add_lptp_bindings(editor, pg) {
  editor.set_key("C-c i s", () => { send_lptp_buffer(pg).then(() => {}); });
  editor.set_key("C-c i g", () => { get_lptp_buffer(pg).then(() => {}); });
  editor.set_key("C-c i S-g", async () => {
    await send_lptp_buffer(pg);
    await get_lptp_buffer(pg);
  });
  editor.set_key("C-c i l", () => { list_lptp_facts(pg).then(() => {}); });
  editor.set_key("C-c i d", () => { print_lptp_definition(pg).then(() => {}); });
  editor.set_key("C-c i m", () => { lptp_mark(pg).then(() => {}); });

  const tactic_list = [
    ['auto', 'C-c i a'],
    ['case', 'C-c i c'],
    ['comp', 'C-c i o'],
    ['elim', 'C-c i e'],
    ['ex', 'C-c i x'],
    ['fact', 'C-c i f'],
    ['ind', 'C-c i i'],
    ['indqf', 'C-c q'],
    ['tot', 'C-c i t'],
    ['unfold', 'C-c u'],
    ['debug', 'C-c i b'],
  ];
  tactic_list.map(([tactic_name, shortcut]) => {
    editor.set_key(shortcut, () => { lptp_tactic(pg, tactic_name).then(() => {}); });
  });
}

// =========================================================================== 
// * Error parser and handler

/**
 * An error.
 * @typedef {Object} Error
 * @property {string} lines - The file where the error comes from.
 * @property {string} msg - Message containing a description of the error.
 */

/**
 * A warning.
 * @typedef {Object} Warning
 * @property {string} lines - The file where the warning comes from.
 * @property {string} msg - Message containing a description of the warning.
 */

/**
 * Message parsed
 * @typedef {Object} MsgObject
 * @property {string} file - The file where the message comes from.
 * @property {Error[]} errors - Array containing all the errors found in the file.
 * @property {Warning[]} warnings - Array containing all the warnings found in the file.
 */

/**
 * Parses the message received from the Prolog side when loading the code and returns
 * it in a sorted object.
 * @param {string} msg - Message generated when loading file.
 * @returns {MsgObject} - Object containing all the info from the message.
 */
// TODO: this parser is incomplete; it does not deal correctly with 'file'
function parse_error_msg(msgs) {
  let warnings = [];
  let errors = [];

  const regexp = /{[^{}]*\b(WARNING|ERROR|FAILED|Reading|In|Compiling|Analyzing|Checking|Loading)\b([^{}]+)}/g;
  const w_regexp = /(Reading|In|Compiling|Analyzing|Checking|Loading)/g; 

  msgs.match(regexp)?.forEach(e =>  {
    let file = undefined; // TODO: not filled!
    let lines = undefined;
    let queue = undefined;
    let group = [];
    const begin_msg = (e) => { // begin new message
      group = [];
      // TODO: poor matching, fix it
      if (e.includes('WARNING')) {
        queue = warnings;
      } else if (e.includes('ERROR') || e.includes('FAILED')) {
        queue = errors;
      } else {
        return;
      }
      let errmsg = e.slice(e.indexOf(':') + 2);
      if (e.includes('lns')) {
        lines = errmsg.slice(errmsg.indexOf('(') + 5, errmsg.indexOf(')'));
        group.push(errmsg.slice(errmsg.indexOf(')') + 2));
      } else { // TODO: keep previous 'lines', is it OK?
        group.push(errmsg);
      }
    };
    const add_msg = (e) => { // add text to the message
      group.push(e);
    };
    const flush_msg = () => { // emit pending message
      if (group.length == 0 || queue == undefined) return;
      queue.push({ file: file, lines: lines, msg: group.join('\n') });
      group = [];
    };
    if (e.match(w_regexp)) { // message in a '{...}' group
      e.split('\n').forEach(line => {
        // TODO: poor matching, fix it
        if (line.includes('WARNING') || line.includes('ERROR') || e.includes('FAILED')) { // begin new message
          flush_msg();
          begin_msg(line);
        } else if (line == '}') {
          flush_msg();
        } else { // just accumulate line in group
          add_msg(line);
        }
      });
      flush_msg();
    } else { // single message in '{...}'
      begin_msg(e.slice(0, e.indexOf('}') - 1));
      flush_msg();
    }
  });
    
  return { warnings: warnings, errors: errors };
}

/* =========================================================================== */
// * Comint (for interacting with a ToplevelProc)

class Comint {
  constructor(container, pg, opts) {
    this.vis = new Vis(); // visibility state

    this.prompt = '?- ';
    this.promptval = ' ? ';
    this.single_query_output = ( opts.onequery === true ? true : false ); // clean output for each query

    this.with_prompt = ( opts.noprompt === true ? false : true ); // interactive
    this.echo_el = elem_cn('span', 'comint-echo'); /* see enable_response */
    this.pg = pg; // associated pgcell
    this.needs_clean_output = true; // queue an output clean
    this.#reset_ring();

    let comint_editor_el = elem_cn('div', 'comint-editor-container');
    //
    const linebtn = (title, text, cmd) => {
      return btn('comint-button', title, text, () => {
        this.#send_line(cmd).then(()=>{});
      });
    }
    this.next_button = linebtn("Next solution", "Next", ';');
    this.stop_button = linebtn("Stop query", "Stop", '');
    this.abort_button = btn('comint-button', "Abort query", "&#10005; Abort", () => {
      const cproc = this.pg.cproc;
      cproc.abort().then(() => {});
    });
    this.abort_button.classList.add('comint-button-abort');
    //
    this.creep_button = linebtn("Continue execution", "Creep", 'c');
    this.leap_button = linebtn("Continue until next spypoint", "Leap", 'l');
    this.skip_button = linebtn("Skip children calls", "Skip", 's');
    this.retry_button = linebtn("Retry this call", "Retry", 'r');
    this.fail_button = linebtn("Force failure", "Fail", 'f');
    //
    this.control_el = elem_cn('div', 'comint-control');
    this.control_el.appendChild(this.next_button);
    this.control_el.appendChild(this.stop_button);
    this.control_el.appendChild(this.creep_button);
    this.control_el.appendChild(this.leap_button);
    this.control_el.appendChild(this.skip_button);
    this.control_el.appendChild(this.retry_button);
    this.control_el.appendChild(this.fail_button);
    this.control_el.appendChild(this.echo_el);
    this.control_el.appendChild(this.abort_button);
    this.control_el.style.display = "none"; // (hidden by default)
    //
    container.replaceChildren();
    container.appendChild(comint_editor_el);
    container.appendChild(this.control_el);

    this.editor = create_pg_editor(comint_editor_el, '{...}', 'toplevel', opts);
    if (!this.with_prompt) this.editor.set_read_only(true);
    this.set_keymap();
  }

  /* ---------------------------------------------------------------------- */

  /* Check if the editor component is focused */
  has_focus() {
    return this.editor.has_focus();
  }

  /* ---------------------------------------------------------------------- */

  /* Update layout of editors */
  update_dimensions() {
    this.editor.update_dimensions();
  }

  /* Update (internal) layout (incrementally) */
  update_inner_layout() {
    // Display/hide buttons based on query state
    //  - "Abort query" if QueryState.RUNNING
    //  - "Next" and "Stop" if QueryState.VALIDATING
    //  - Debug buttons if QueryState.DBGTRACE
    const cproc = this.pg.cproc;
    // TODO: when debugging is activated, replace abort by 'stop' (as C-c)
    this.vis.set('abort_button', (cproc !== undefined && cproc.state === QueryState.RUNNING));
    this.vis.set('nextstop_btns', (cproc !== undefined && cproc.state === QueryState.VALIDATING));
    this.vis.set('debug_btns', (cproc !== undefined && cproc.state === QueryState.DBGTRACE));
    this.vis.inc_update('abort_button', (st) => {
      this.abort_button.style.display = st ? "inline-block" : "none";
    });
    this.vis.inc_update('nextstop_btns', (st) => {
      this.next_button.style.display = st ? "inline-block" : "none";
      this.stop_button.style.display = st ? "inline-block" : "none";
    });
    this.vis.inc_update('debug_btns', (st) => {
      this.creep_button.style.display = st ? "inline-block" : "none";
      this.leap_button.style.display = st ? "inline-block" : "none";
      this.skip_button.style.display = st ? "inline-block" : "none";
      this.retry_button.style.display = st ? "inline-block" : "none";
      this.fail_button.style.display = st ? "inline-block" : "none";
    });
    // Show control button menu if needed
    this.vis.set('control', (this.vis.get('abort_button') || this.vis.get('nextstop_btns') || this.vis.get('debug_btns')));
    this.vis.inc_update('control', (st) => {
      this.#show_control(st);
    });
  }

  #show_control(st) {
    if (st) { // show control
      if (this.control_show_timer !== undefined) return;
      // show control after a brief delay
      this.control_show_timer = setTimeout(() => {
        this.control_el.style.display = "inline-block";
        setTimeout(() => { // then reveal toplevel end line after a delay // TODO: better way?
          this.reveal_end();
        }, 0);
        this.update_dimensions();
      }, 100);
    } else { // hide control
      if (this.control_show_timer !== undefined) {
        clearTimeout(this.control_show_timer); // cancel timeout
        this.control_show_timer = undefined;
      }
      if (this.control_el.style.display !== "none") {
        this.control_el.style.display = "none";
        // setTimeout(() => { // then reveal toplevel end line after a delay // TODO: better way?
        //   this.reveal_end();
        // }, 0);
        this.update_dimensions();
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Current input query */

  /* Get the current query text (removing the prompt mark) */
  #current_input() {
    let line = this.editor.get_line_num();
    let content = this.editor.get_line_content(line);

    if (content.slice(0, this.prompt.length) === this.prompt) { // normal single line query
      return content.slice(this.prompt.length);
    } else {
      if (content.slice(-1) === '.') { // several line query
        // obtain code from prompt to '.'
        line--;
        while (line !== 0 && content.slice(0, this.prompt.length) !== this.prompt) {
          content = this.editor.get_line_content(line) + '\n' + content;
          line--;
        }
        return content.slice(3);
      } else {
        return content;
      }
    }
  }

  /* Get the current solution validation text */
  #current_inputVal() {
    let line = this.editor.get_line_num();
    if (line !== this.editor.get_line_count()) {
      return ''; // Like 'Enter' if not the last line
    } else { // Whatever comes after the last ' ? ' in the line (promptval)
      let text = this.editor.get_line_content(line);
      let i = text.lastIndexOf(this.promptval);
      if (i === -1) {
        return ''; // TODO: promptval not found, just ''?
      } else {
        return text.slice(i + this.promptval.length);
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Output (basic) */

  /* Clear comint output */
  clear_output() {
    this.editor.set_value("");
    this.needs_clean_output = false;
  }
  /* Write at the end of the current editor position */
  display(text) {
    this.move_to_end();
    this.editor.insert_text(text);
  }
  /* Moves cursor to the end */
  move_to_end() {
    const nLines = this.editor.get_line_count();
    this.editor.set_position(this.editor.get_line_length(nLines) + 1, nLines);
  }
  /* Moves cursor to the end of first line */
  move_to_first_line_end() {
    this.editor.set_position(this.editor.get_line_length(1) + 1, 1);
  }
  /* Reveal the beginning of the editor */
  reveal_first() {
    this.editor.reveal_line(1);
  }
  /* Reveal the end of the editor (scrolling down) */
  reveal_end() {
    this.editor.reveal_line(this.editor.get_line_count());
  }

  /* Clear all text before the current line */
  clear_text_above() {
    let text = this.#current_input();
    this.clear_output();
    this.#add_prompt();
    this.display(text);
  }

  /* ---------------------------------------------------------------------- */
  /* Output (derived) */

  print_out(str) {
    this.display(str);
  }
  print_sol(str) {
    this.display('\n'+str);
  }
  print_msg(str) {
    this.display(str);
  }

  /* ---------------------------------------------------------------------- */
  /* Output (for interactive) */

  #add_prompt() {
    this.display(this.prompt);
  }
  print_prompt() {
    /* Clean output if needed, write the prompt, move to end */
    if (this.needs_clean_output) {
      this.clear_output();
    }
    this.pg.mark_srcdbg_info(null); /* TODO: better place? */
    if (!this.with_prompt) return; /* (skip in non-interactive) */
    this.#add_prompt();
  }
  print_promptval() {
    if (!this.with_prompt) return; /* (skip in non-interactive) */
    this.display(this.promptval);
  }
  display_status(str) { // toplevel:display_status/1
    if (!this.with_prompt) return; /* (skip in non-interactive) */
    if (str === 'silent') return; /* skip this status */
    this.display('\n');
    this.display(str);
    if (!this.single_query_output) this.display('\n');
  }
  display_status_new_prompt(str) { /* show status and a new prompt */
    this.display_status(str);
    if (this.single_query_output) {
      this.move_to_first_line_end();
      this.reveal_first();
    } else {
      this.print_prompt();
      // this.reveal_end(); // TODO: needed?
    }
  }
  set_log(text) {
    const e = this.echo_el;
    if (e !== null) {
      e.className = 'comint-echo';
      e.classList.add(text === '' ? 'comint-echo-hidden' : 'comint-echo-running');
      e.innerHTML = text === '' ? '' : text+' ...';
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Command ring (input) */

  /* Reset/initialize input ring. */
  #reset_ring() {
    this.ring = [''];
    this.ringCurr = 0;
  }

  /* Add text to the ring array containing previous queries done by the user. */
  #ring_push(text) {
    this.ring.push('');
    this.ringCurr = this.ring.length - 1;
    this.ring[this.ringCurr - 1] = text;
    //
    this.outputMarker = text; // TODO: always called after #ring_push, is it OK here?
  }

  /* Move through previous queries. It writes the previous query, if any. */
  #ring_up() {
    if (this.ringCurr === 0) { // No more ring
      return;
    }
    let curr = this.#current_input();
    if (this.ringCurr === this.ring.length - 1) { // Save current edit
      this.ring[this.ringCurr] = curr;
    }
    this.ringCurr--;
    if (curr === '' || curr === this.outputMarker) {
      this.outputMarker = this.ring[this.ringCurr];
      this.#delete_curr_query(); // delete query at the current line
      this.display(this.ring[this.ringCurr]);
    } else {
      return;
    }
  }

  /* Move through previous queries. It goes down to next query, if any. */
  #ring_down() {
    if (this.ringCurr >= this.ring.length - 1) { // No more elements
      return;
    }
    this.ringCurr++;

    if (this.#current_input() === this.outputMarker) {
      this.outputMarker = this.ring[this.ringCurr];
      this.#delete_curr_query(); // delete query at the current line
      this.display(this.ring[this.ringCurr]);
    }

    if (this.ringCurr === this.ring.length - 1) {
      // Clear current edit (only for saving memory)
      this.ring[this.ringCurr] = "";
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Other movement */

  /* Avoid moving the cursor to the prompt. */
  #treat_line_start() {
    let lineNumber = this.editor.get_line_num();
    let col = (this.editor.get_line_content(lineNumber).slice(0, this.prompt.length) === this.prompt) ? this.prompt.length+1 : 0;
    this.editor.set_position(col, lineNumber);
  }

  /* Avoid moving the cursor to the prompt. */
  #treat_left_arrow() {
    let pos = this.editor.get_position();
    if (pos.col == this.prompt.length+1 &&
        this.editor.get_line_content(pos.row).slice(0, this.prompt.length) === this.prompt) {
      return;
    } else {
      this.editor.set_position(pos.col - 1, pos.row);
    }
  }

  /* ---------------------------------------------------------------------- */

  /* Enter is pressed (get input and treat it) */
  async treat_enter() {
    const cproc = this.pg.cproc;
    if (!cproc.check_not_running()) return;
    if (cproc.comint !== this) {
      // Deal with previously running queries (not in this comint)
      await this.#ensure_no_pending_query();
    }
    let text;
    if (cproc.is_waiting_for_line()) {
      text = this.#current_inputVal();
      this.display('\n');
      this.reveal_end();
    } else {
      text = this.#current_input();
      // if (text === '') return; // TODO: make it optional? disallow empty prompt lines
      if (text !== '') this.#ring_push(text); // Add to ring if not empty
      if (this.single_query_output) { // only one query at a time in output
        this.clear_output();
        this.#add_prompt();
        if (text === '') {
          // do nothing
        } else {
          // clean output
          this.display(text);
          this.display('\n');
          this.reveal_end();
        }
      } else {
        // Duplicate text if input is not from last line
        if (this.editor.get_line_num() !== this.editor.get_line_count()) {
          this.display(text);
        }
        this.display('\n');
        this.reveal_end();
      }
    }
    await this.#treat_enter_(text);
  }

  async #treat_enter_(text) {
    const cproc = this.pg.cproc;
    if (cproc.is_waiting_for_line()) {
      if (!cproc.check_not_locked(this)) return; // TODO: not possible?
      await cproc.send_line(this, text);
    } else {
      // Perform query
      if (text === '') {
        if (!this.single_query_output) { // show prompt again
          this.print_prompt();
          this.reveal_end(); // scroll down
        }
      } else {
        if (text.slice(-1) !== '.') { // query is malformed
          this.display(`\
{SYNTAX ERROR: Malformed query. It must end with a period.}
{NOTE: Press Shift+Enter to continue a query in the next line.}
`);
          this.display_status_new_prompt('silent');
        } else {
          let q = text.slice(0, -1);
          await cproc.run_query(this, q, {});
        }
      }
    }
  }

  // Cancel any query pending for validation of debugging
  async #ensure_no_pending_query() {
    const cproc = this.pg.cproc;
    if (cproc.state === QueryState.VALIDATING) { // If validating, just accept and continue
      // TODO: do this in the ciao-emacs mode? or remove this feature?
      await cproc.comint.#send_line('');
    }
    if (cproc.state === QueryState.DBGTRACE) {
      // await cproc.comint.#send_line('a'); // abort // TODO: fix 'abort' (it should not end the engine)
      // workaround: leap and try ending the query gracefully
      await cproc.comint.#send_line('l');
      await this.#ensure_no_pending_query();
    }
  }

  /* ---------------------------------------------------------------------- */

  /* Emacs key bindings and custom for comint */
  set_keymap() {
    if (this.with_prompt) {
      [ 
        ["UP", () => { this.#ring_up(); }], // Ring up (up arrow)
        ["C-p", () => { this.#ring_up(); }], // Ring up (C-p)
        ["DOWN", () => { this.#ring_down(); }], // Ring down (down arrow)
        ["C-n", () => { this.#ring_down(); }], // Ring down (C-n)
        ["C-l", () => { this.clear_text_above(); }], // Clean screen (C-l)
        ["C-a", () => { this.#treat_line_start(); }], // Move cursor to the beginning of line, avoid the prompt
        ["LEFT", () => { this.#treat_left_arrow(); }], // Move left, avoid the prompt
        ["RET", () => { this.treat_enter().then(() => {}); }] // Treat the enter binding to run the written query (Enter)
      ].forEach(([key, handler]) => this.editor.set_key(key, handler));
      // Other playground commands
      add_playground_bindings(this.editor, this.pg);
    }

    // Abort running query (C-c C-c)
    this.editor.set_key("C-c C-c", () => {
      const cproc = this.pg.cproc;
      if (cproc.state === QueryState.RUNNING) {
        this.display('C-c C-c\n');
        cproc.abort().then(() => {});
      }
    });
  }

  /* ---------------------------------------------------------------------- */

  /* Go to the end of the current line */
  // TODO: unused?
  #go_to_line_end() {
    let line = this.editor.get_line_num();
    let length = this.editor.get_line_length(line); // length of the line
    this.editor.set_position(length + 1, line);
  }

  /**
   * Get line with the closest prompt.
   * @returns {number} Number of the closest line with the prompt.
   */
  #get_line_with_prompt() {
    const currentLine = this.editor.get_line_num();
    if (this.editor.get_line_content(currentLine).slice(0, this.prompt.length) === this.prompt) {
      return currentLine;
    } else {
      let lineWithPrompt = currentLine - 1;
      while (lineWithPrompt !== 0 && 
             this.editor.get_line_content(lineWithPrompt).slice(0, this.prompt.length) !== this.prompt) {
        lineWithPrompt--;
      }
      return lineWithPrompt;
    }
  }

  /* ---------------------------------------------------------------------- */

  /* Delete text at the current prompt */
  #delete_curr_query() {
    if (this.single_query_output) { // only one query at a time in output
      this.clear_output();
      this.#add_prompt();
    } else {
      // Discard the current query content (if any)
      this.move_to_end();
      let line = this.editor.get_line_num();
      this.editor.remove_range(4, this.#get_line_with_prompt(), 1, line+1); // TODO: 4 is length of prompt?
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Queries and validation */

  /* Send validation or dbgcmd text */
  async #send_line(text) {
    const cproc = this.pg.cproc;
    if (!cproc.is_waiting_for_line()) {
      console.log('bug: not validating/debugging');
      return;
    }
    if (!cproc.check_not_locked(this)) return; // TODO: not possible?
    this.display(text);
    this.display('\n');
    this.reveal_end();
    await cproc.send_line(this, text);
  }

  /* Send a query (optionally) printing it */
  /*  - opts.msg (optional): log message */
  async do_query(q, opts) {
    const cproc = this.pg.cproc;
    if (cproc.state === QueryState.RUNNING) {
      console.log('bug: already running'); // TODO: treat_enter too fast?
      return; // TODO: query is lost!
    }
    await this.#ensure_no_pending_query();
    //
    if (this.with_prompt && !cproc.muted) this.#add_query(q);
    await cproc.run_query(this, q, opts);
  }

  /**
   * Add query to output (like if the user wrote it)
   */
  #add_query(q) {
    let text = q + ".";
    this.#ring_push(text); // Add to ring // TODO: make optional
    this.#delete_curr_query();
    this.display(text);
    this.display('\n');
    this.reveal_end();
  }
}

/** Console based Comint (reduced functionality) */
class ConsoleComint {
  constructor(pg) {
    this.pg = pg; // associated pgcell
    this.with_prompt = false;
  }
  set_log(str) {
    if (str == '') return;
    console.log('[LOG: '+str+']');
  }
  //
  has_focus() { return false; }
  //
  update_dimensions() {}
  update_inner_layout() {}
  //
  display_status_new_prompt(str) {}   
  print_promptval() {}
  //
  print_out(str) {
    console.log(str);
  }
  print_sol(str) {
    console.log('\n'+str);
  }
  print_msg(str) {
    console.log(str);
  }
  // async #send_line(text) // (not yet)
}

// ===========================================================================
// * Code persistence (in web local storage)

/**
 * Obtain the value of the item with the key `code`, containing the
 * most recent code stored in the local storage.
 * @return {string} - Latest code stored in the local storage or null if it is
 * empty or there was an error.
 */
function pers_get_code(pgCfg) {
  const k = pgCfg.storage_key;
  if (k === null) return null;
  try {
    let str = window.localStorage.getItem(k+".str");
    let ext = window.localStorage.getItem(k+".ext");
    if (str === null) return null;
    return {str:str, ext:ext, origin:'storage'};
  } catch (e) {
    // Swallow up any security exceptions...
    return null;
  }
}

/**
 * Store the `value` passed as a parameter in the key `code` in the
 * local storage.
 * @param {string} value - Code to store in the local storage
 * @returns {boolean} - True if it was stored correctly or false if there was an 
 * error.
 */
function pers_set_code(pgCfg, code) {
  const k = pgCfg.storage_key;
  if (k === null) return true;
  try {
    window.localStorage.setItem(k+".str", code.str);
    window.localStorage.setItem(k+".ext", code.ext);
    return true;
  } catch (e) {
    // Swallow up any security exceptions...
    return false;
  }
}

/**
 * Delete the item `code` in the local storage.
 * @returns {boolean} - True if it was stored correctly or false if there was an
 * error.
 */
function pers_remove_code(pgCfg) {
  const k = pgCfg.storage_key;
  if (k === null) return true;
  try {
    window.localStorage.removeItem(k+".str");
    window.localStorage.removeItem(k+".ext");
    return true;
  } catch (e) {
    // Swallow up any security exceptions...
    return false;
  }
}

// ---------------------------------------------------------------------------
// * Set of playground cells

class PGSet {
  constructor() {
    this.cells = [];
    this.cproc = new ToplevelProc(urlPREFIX+'/ciao/', urlVERS); // (shared)
  }

  attach_main_doc(main_doc) {
    main_doc.pgset = this; // (associated PGSet)
    this.main_doc = main_doc; // TODO: pass doc to jscode instead?
    main_doc.update_dimensions_hook = () => { this.update_dimensions(); };
    main_doc.has_focus_hook = () => { return this.has_focus(); };
  }  

  async setup(base_el, code) { // standalone playground
    this.base_el = base_el;
    let i = 0;
    this.main_doc.runnables = [];
    //
    this.cells[i] = new PGCell(this.cproc);
    if (code == null) code = await initial_editor_code(this.cells[i].pgCfg);
    // Show splash screen based on code origin annotation
    // TODO: show splash with '?' button, add link to help there
    if (code.origin === 'splash') {
      show_splash(base_el);
    }
    let cell_data = { kind:'full', focus: code.str, ext: code.ext, origin: code.origin };
    this.main_doc.runnables[i] = cell_data;
    await this.cells[i].setup(base_el, cell_data, this);
  }

  async setup_runnable(base_el) { // playground cells for an LPdoc document
    this.main_doc.runnables = [];
    let i = 0;
    for (let node of [...base_el.getElementsByClassName("lpdoc-codeblock-runnable")]) {
      let txt = node.innerText;
      // TODO: we need to amend wrong double quotes generated from LPdoc code code blocks (fix in lpdoc!)
      txt = txt.replace(/\u201D/g, "''"); // &rdquo;
      //
      const cell_data = scan_runnable(txt);
      cell_data.modname = "code_"+i;
      const el = document.createElement('div');
      node.replaceWith(el);
      this.main_doc.runnables[i] = cell_data;
      this.cells[i] = new PGCell(this.cproc);
      await this.cells[i].setup(el, cell_data, this);
      i += 1;
    }
  }

  // Any of the cells has focus
  has_focus() {
    for (const x of this.cells) {
      if (x.has_focus()) return true;
    }
    return false;
  }

  // Update layout of editors
  update_dimensions() {
    for (const x of this.cells) {
      x.update_dimensions();
    }
  }

  async reset_all_code() {
    for (const x of this.cells) {
      if (x.cell_data !== undefined && x.cell_data.kind == 'code') {
        x.set_code_status('unknown');
        // // Load all cells // TODO: make it configurable (add to jseval_trick.md)
        // await x.with_response(load_code);
      }
    }
  }
}

function scan_runnable(text) {
  let cell_data = {};
  // TODO: ugly regexp based solution, syntax is fixed
  // hint+solution regexp
  const re_hs = /(.*)^%![\s]*\\begin{hint}(.*)^%![\s]*\\end{hint}[\s]*^%![\s]*\\begin{solution}(.*)^%![\s]*\\end{solution}[\s]*(.*)/sgm;
  // focus regexp
  const re_f = /(.*)^%![\s]*\\begin{focus}(.*)^%![\s]*\\end{focus}[\s]*(.*)/sgm;
  // miniplayground regexp
  const re_mp = /(.*)^%![\s]*\\begin{miniplayground}(.*)^%![\s]*\\end{miniplayground}[\s]*(.*)/sgm;
  // query regexp
  //const re_q = /(.*)^%![\s]*\\begin{query}[\s]*\?-[\s]*(.*)^%![\s]*\\end{query}[\s]*(.*)/sgm;
  const re_q = /[\s]*\?-[\s]*(.*)/sgm;
  // jseval regexp
  const re_jse = /(.*)^%![\s]*\\begin{jseval}(.*)^%![\s]*\\end{jseval}[\s]*(.*)/sgm;
  // dynpreview regexp
  const re_dynpreview = /(.*)^%![\s]*\\begin{dynpreview}(.*)^%![\s]*\\end{dynpreview}[\s]*(.*)/sgm;
  // exfilter exercises
  const re_exfilter_ex = /(.*)^%![\s]*\\begin{code}(.*)^%![\s]*\\end{code}[\s]*^%![\s]*\\begin{opts}(.*)^%![\s]*\\end{opts}[\s]*^%![\s]*\\begin{solution}(.*)^%![\s]*\\end{solution}[\s]*(.*)/sgm;
  // exfilter
  const re_exfilter = /(.*)^%![\s]*\\begin{code}(.*)^%![\s]*\\end{code}[\s]*^%![\s]*\\begin{opts}(.*)^%![\s]*\\end{opts}[\s]*(.*)/sgm;
  //  
  let match;
  match = re_hs.exec(text);
  if (match !== null) {
    cell_data.kind = 'exercise';
    cell_data['ext'] = '.pl';
    cell_data['preamble'] = match[1].trim();
    cell_data['focus'] = match[2].trim();
    cell_data['solution'] = match[3].trim();
    cell_data['postamble'] = match[4].trim();
    return cell_data;
  }
  match = re_f.exec(text);
  if (match !== null) {
    cell_data.kind = 'code';
    cell_data['ext'] = '.pl';
    cell_data['preamble'] = match[1].trim();
    cell_data['focus'] = match[2].trim();
    cell_data['postamble'] = match[3].trim();
    return cell_data;
  }
  match = re_exfilter_ex.exec(text);
  if (match !== null) {
    cell_data.kind = 'exfilterex';
    cell_data['ext'] = '.pl';
    cell_data['preamble'] = match[1].trim(); 
    cell_data['focus'] = match[2].trim();
    cell_data['opts'] = match[3].trim();
    cell_data['solution'] = match[4].trim();
    cell_data['postamble'] = match[5].trim();
    return cell_data;
  }
  match = re_exfilter.exec(text);
  if (match !== null) {
    cell_data.kind = 'exfilter';
    cell_data['ext'] = '.pl';
    cell_data['preamble'] = match[1].trim(); 
    cell_data['focus'] = match[2].trim();
    cell_data['opts'] = match[3].trim();
    cell_data['postamble'] = match[4].trim();
    return cell_data;
  }
  match = re_mp.exec(text);
  if (match !== null) {
    cell_data.kind = 'miniplayground';
    cell_data['ext'] = '.pl';
    cell_data['preamble'] = match[1].trim();
    cell_data['focus'] = match[2].trim();
    cell_data['postamble'] = match[3].trim();
    return cell_data;
  }
  match = re_jse.exec(text);
  if (match !== null) {
    cell_data.kind = 'jseval';
    //cell_data['preamble'] = match[1].trim();
    cell_data['jscode'] = match[2].trim();
    //cell_data['postamble'] = match[3].trim();
    return cell_data;
  }
  match = re_dynpreview.exec(text); // (expand as jseval)
  if (match !== null) {
    cell_data.kind = 'jseval';
    cell_data['jscode'] = "async(pg) => { await pg.setup_dynpreview("+match[2].trim()+"); }";
    return cell_data;
  }
  match = re_q.exec(text);
  if (match !== null) {
//    cell_data['preamble'] = match[1].trim();
//    cell_data['query'] = match[2].trim();
//    cell_data['postamble'] = match[3].trim();
    cell_data.kind = 'query';
    cell_data['query'] = match[1].trim();
    return cell_data;
  }
  // Assume all is the 'focus'
  cell_data.kind = 'code';
  cell_data['ext'] = '.pl';
  cell_data['preamble'] = '';
  cell_data['focus'] = text;
  cell_data['postamble'] = '';
  return cell_data;
}

// Initial editable code from cell data
function initial_code_from_cell_data(cell_data) {
  switch(cell_data.kind) {
  case 'exercise': break;
  case 'code': break;
  case 'exfilter': break;
  case 'exfilterex': break;
  case 'miniplayground': break;
  case 'full': break;
  default: return null; /* no editable code */
  }
  return {str:cell_data['focus'], ext:cell_data['ext'], origin:'runnable'};
}

// ===========================================================================
// * Set up mathjax (dynamically)

// TODO: integrate or move closer to lpdoc.js?

function setup_mathjax() {
  window.MathJax = {
    tex: {
      inlineMath: [['$', '$']],
      macros: {
        infin: "{\\infty}",
        empty: "{\\emptyset}"
      }
    },
    options: {
      renderActions: {
        /* add a new named action not to override the original 'find' action */
        find_script_mathtex: [10, function (doc) {
          for (const node of document.querySelectorAll('script[type^="math/tex"]')) {
            const display = !!node.type.match(/; *mode=display/);
            const math = new doc.options.MathItem(node.textContent, doc.inputJax[0], display);
            const text = document.createTextNode('');
            node.parentNode.replaceChild(text, node);
            math.start = { node: text, delim: '', n: 0 };
            math.end = { node: text, delim: '', n: 0 };
            doc.math.push(math);
          }
        }, '']
      }
    }
  };
  
  (function() {
    let script = document.createElement('script');
    script.src = urlPREFIX+'/node_modules/mathjax/es5/tex-svg.js';
    script.async = true;
    document.head.appendChild(script);
  })();
}

// ===========================================================================
// * UI - cover window

// A cover div (just in case it needs time to load).  There is a fade
// in animation (see CSS for 'covermsg') so that quick loads are not
// disturbed by messages.

var cover_div = null;

function show_cover() {
  window.addEventListener('DOMContentLoaded', (event) => {
    cover_div = elem_from_str("<div id='cover'><span id='covermsg'>Loading ... This can take a few minutes on slow connections.</span></div>");
    document.body.appendChild(cover_div);
  });
}
function hide_cover() {
  if (cover_div !== null) {
    cover_div.remove();
    cover_div = null;
  }
}

// ===========================================================================
// * UI - splash modal dialog

function show_splash(base_el) {
  let modal = new Modal();
  modal.alloc(base_el);
  const m = modal.get_elm();
  m.replaceChildren();
  //
  const splash = elem_from_str(`
<div style='padding: 24px;'>
<img src="/playground/images/ciao.png"
     style="display:block; margin-left:auto; margin-right:auto;"
     height="64px"
     alt="Ciao logo">
<h2 style='text-align:center'>Welcome to the Ciao Prolog Playground!</h2>

<p><a href="/"><b>Ciao</b></a> is a modern <b>Prolog</b> system
designed to write safe, correct, and efficient Prolog programs. You can
use this code <b>playground</b> to learn more about it.</p>

<p>Under the hood, a <a href="/ciao/build/doc/ciao.html/"><b>richly featured</b></a> Ciao system compiled to <a
href="https://en.wikipedia.org/wiki/WebAssembly"><b>WebAssembly</b></a>
is running fully locally inside your browser!</p>
</div>
`);
  // Big close button
  const el = btn('big-button', "Start Coding", "Start Coding", () => {
    modal.close();
  });
  const btn_w = elem_cn('div', 'big-button-wrapper');
  btn_w.appendChild(el);
  splash.appendChild(btn_w);
  const disclaimer = elem_from_str(`
<div class="disclaimer">
Please consult the current playground <a
href="/ciao/build/doc/ciao_playground.html/ciao_playground_using.html#Current%20playground%20limitations">limitations</a> and
consider <a href="/install.html">native installation</a> if needed.
</div>`);
  splash.appendChild(disclaimer);
  //
  m.appendChild(splash);
  modal.open();
}

// ===========================================================================
// * UI - Playground header

/* Header (with top-right menu) */
function create_header(pgCfg) {
  let header_el = elem_cn('div', 'header');
  let right_header_el = elem_cn('ul', 'right-header');
  header_el.appendChild(right_header_el);
  /* assume / points to https://ciao-lang.org */
  header_el.appendChild(elem_from_str(`\
    <div class="title">
      <a href="/">
        <img id="logo" src="/playground/images/ciao.png" alt="Ciao logo">
      </a>
      <h1>${pgCfg.title}</h1>
    </div>`));

  const theme_button = new_theme_button(right_header_el);
  theme_button.highlight(theme_get_value());

  function a(href, text) {
    const el0 = document.createElement('li');
    const el = document.createElement('a');
    el.href=href;
    el.appendChild(text);
    el0.appendChild(el);
    return el0;
  }
  // right_header_el.appendChild(a("/ciao/build/doc/ciao_playground.html/", "Manual"));
  right_header_el.appendChild(a("/ciao/build/doc/ciao_playground.html/ciao_playground_using.html", get_icon('help')));
  right_header_el.appendChild(a("/ciao/build/doc/ciao_playground.html/ciao_playground_embedding.html", document.createTextNode("Embed")));
  // right_header_el.appendChild(a("/ciao/build/doc/ciao.html/", document.createTextNode("Docs")));
  right_header_el.appendChild(a("/documentation.html", document.createTextNode("Docs")));
  if (pgCfg.with_github_stars) {
    setup_github_stars(right_header_el);
  } else {
    right_header_el.appendChild(a("https://github.com/ciao-lang/ciao", get_icon('github')));
  }
  return header_el;
}

// ===========================================================================
// * Startup code

if (window.lpdocPG === 'runnable') setup_mathjax();
if (window.lpdocPG === 'playground') show_cover();

var globalPlaygroundCfg;

window.onload = function () {
  if (window.lpdocPG === 'playground') hide_cover();

  globalPlaygroundCfg = (typeof window.playgroundCfg !== 'undefined') ? window.playgroundCfg : {};

  // Set CSS theme as soon as possible to avoid flickering
  // Editor theme is selected on creation. It will be updated if needed.
  update_theme_hook();

  if (window.lpdocPG !== 'raw') {
    // (collection of playgrounds for this document)
    var pgset = new PGSet();
    pgset.attach_main_doc(main_doc);
    const base_el = document.body;
    (async() => {
      if (window.lpdocPG === 'runnable') { // LPdoc with runnable code
        await pgset.setup_runnable(base_el);
      } else { // Full playground
        await pgset.setup(base_el, null);
      }
      main_doc.update_dimensions();
    })();
  }
};

globalThis.PGCell = PGCell; // TODO: turn into a proper module (required for multi.html)

// Local Variables:
// eval: (outshine-mode)
// End:
